import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import * as React from "react";
// import { useBlocker } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Zap, Plus, FileText, Download, Trash2, Loader2, Calculator, Eye, CheckCircle, FileSignature, Pencil, ArrowUpDown, ArrowUp, ArrowDown, Save, Calendar, Package, Gauge, Search } from "lucide-react";
import { formatBrazilianCurrency, formatPhone } from "@/utils/formatters";
import { CarregadoresMoneyInput } from "@/components/CarregadoresMoneyInput";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuthorization } from "@/contexts/AuthorizationContext";
import { useIsMobile } from "@/hooks/use-mobile";
import AdicionarLead from "@/components/ClientDetails/AdicionarLead";
import { ClientDetails } from "@/components/ClientDetails";
import { CLIENTE_DOCUMENTOS_QUERY_KEY } from "@/components/ClientDetails/Documentos";
import { ExtendedLead } from "@/components/ClientDetails/types";
import ContratoCarregadorFormulario from "@/components/contratos/ContratoCarregadorFormulario";
import { anexarContratoPrincipalCarregador } from "@/utils/contratosCarregadoresUtils";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { ComissoesCarregadoresTab } from "@/components/carregadores/ComissoesCarregadoresTab";
import {
  PRODUTO_LEAD_WALLBOX,
  PRODUTO_LEAD_ESTUDO_VIABILIDADE_EV,
  PRODUTO_LEAD_IMPLANTACAO_SAVE_CONDOMINIOS,
  PRODUTO_LEAD_PROJETO_APROVACAO_SAVE,
  PRODUTOS_LEAD_CARREGADOR,
  PRODUTOS_LEAD_CARREGADOR_SEM_LEGADO,
  isProdutoEstudoViabilidadeEv,
  isProdutoImplantacaoSaveCondominios,
  isProdutoProjetoAprovacaoSave,
  isProdutoInstalacaoWallboxCondominio,
  formatLeadNomeComProduto,
} from "@/constants/leadsProduto";
import { SaveEtapaPagamentoField } from "@/components/carregadores/SaveEtapaPagamentoField";
import { SaveItemSwitchField } from "@/components/carregadores/SaveItemSwitchField";
import { SaveCargaEstudoField } from "@/components/carregadores/SaveCargaEstudoField";
import { WallboxCondominioIntervalosField } from "@/components/carregadores/WallboxCondominioIntervalosField";
import { WallboxItensField } from "@/components/carregadores/WallboxItensField";
import { WallboxExtrasField } from "@/components/carregadores/WallboxExtrasField";
import {
  intervalosPadraoWallboxCondominio,
  validarIntervalosWallboxCondominio,
  rotuloDistanciaIntervalo,
  totalIntervaloWallboxCondominio,
} from "@/utils/wallboxCondominioPropostaUtils";
import {
  extrasPadraoWallboxProposta,
  extrasWallboxIncluidos,
  serializarExtrasWallboxProposta,
  somaExtrasWallboxProposta,
  subtotalExtraWallbox,
  validarExtrasWallboxProposta,
} from "@/utils/wallboxExtrasPropostaUtils";
import { modelosCarregadoresService } from "@/services/modelosCarregadoresService";
import { nomeExibicaoModeloCarregador } from "@/types/modelosCarregadores";
import {
  agregarWallboxItensParaColunas,
  criarItemWallboxProposta,
  itemPadraoWallboxProposta,
  quantidadePropostaCarregador,
  rotuloModeloPropostaCarregador,
  rotuloPotenciaPropostaCarregador,
  serializarWallboxItensProposta,
  somaEquipamentoWallboxItens,
  somaQuantidadeWallboxItens,
  subtotalPropostaWallboxCompartilhado,
  validarWallboxItensProposta,
  type WallboxPropostaItem,
} from "@/utils/wallboxItensPropostaUtils";
import {
  calcularParcelaTabelaPrice,
  saveEtapasSomamTotal,
  saveSomaEtapasEmReais,
  saveValorEtapaEmReais,
  parseMesesGarantiaSave,
  SAVE_GARANTIA_SERVICO_MESES_PADRAO,
  SAVE_GARANTIA_CLAMPER_MESES_PADRAO,
  type SavePgtoTipo,
} from "@/utils/saveCondominiosPropostaUtils";
import {
  SAVE_CARGA_POTENCIA_PADRAO_KW,
  rotuloSaveCargaModo,
  validarSaveCargaEstudo,
} from "@/utils/saveCargaPropostaUtils";

function textoProdutoLeadCarregador(produto: string | null | undefined): string {
  if (produto == null || produto === "") return "—";
  return produto;
}

/** Status permitidos para contratos de carregadores (em aberto = não finalizado) */
export const STATUS_CONTRATO_CARREGADOR = [
  { value: "contrato_gerado", label: "Não assinado", emAberto: true },
  { value: "assinado", label: "Assinado", emAberto: true },
  { value: "em_andamento", label: "Em andamento", emAberto: true },
  { value: "finalizado", label: "Finalizado", emAberto: false },
] as const;

function getContratoStatusLabel(status: string | null | undefined): string {
  if (!status) return "—";
  const s = STATUS_CONTRATO_CARREGADOR.find((o) => o.value === status);
  return s?.label ?? status;
}

function isContratoEmAberto(status: string | null | undefined): boolean {
  if (!status) return true;
  const s = STATUS_CONTRATO_CARREGADOR.find((o) => o.value === status);
  return s?.emAberto ?? true;
}

interface PropostaCarregador {
  id: string;
  lead_id: string;
  created_at: string;
  modelo_carregador: string;
  potencia_kw: number;
  preco_equipamento: number;
  preco_instalacao: number;
  preco_materiais: number;
  preco_projeto?: number | null;
  valor_total: number;
  valor_desconto?: number | null;
  desconto_observacao?: string | null;
  status: string;
  proposta_pdf_url: string | null;
  ev_qtd_transformadores?: string | null;
  ev_qtd_estudos?: number | null;
  ev_incluir_termografia?: boolean | null;
  ev_valor_termografia?: number | null;
  ev_qtd_termografia?: number | null;
  ev_incluir_projeto_eletrico?: boolean | null;
  ev_valor_projeto_eletrico?: number | null;
  ev_qtd_carregadores_projeto?: number | null;
  ev_valor_estudo?: number | null;
  ev_incluir_laudo_spda?: boolean | null;
  ev_valor_laudo_spda?: number | null;
  ev_qtd_laudo_spda?: number | null;
  ev_incluir_compra_analisador?: boolean | null;
  ev_valor_compra_analisador?: number | null;
  ev_qtd_compra_analisador?: number | null;
  ev_incluir_analise_bombeiros?: boolean | null;
  save_preco_projeto_aprovacao?: number | null;
  save_preco_adequacoes_eletricas?: number | null;
  save_preco_adequacoes_in23?: number | null;
  save_preco_outras_adequacoes?: number | null;
  save_qtd_unidades?: number | null;
  save_pgto_entrada_tipo?: string | null;
  save_pgto_entrada_valor?: number | null;
  save_pgto_inicio_tipo?: string | null;
  save_pgto_inicio_valor?: number | null;
  save_pgto_conclusao_tipo?: string | null;
  save_pgto_conclusao_valor?: number | null;
  save_parc_entrada?: number | null;
  save_parc_num_parcelas?: number | null;
  save_parc_juros_am?: number | null;
  save_parc_valor_parcela?: number | null;
  save_garantia_servico_meses?: number | null;
  save_garantia_clamper_meses?: number | null;
  save_carga_modo?: string | null;
  save_carga_demanda_maxima_kw?: number | null;
  save_carga_demanda_minima_kw?: number | null;
  save_carga_demanda_media_kw?: number | null;
  save_carga_demanda_ramal_kw?: number | null;
  save_carga_qtd_pontos?: number | null;
  save_carga_potencia_ponto_kw?: number | null;
  save_carga_demanda_verao_kw?: number | null;
  psave_preco_celesc?: number | null;
  psave_preco_cbmsc?: number | null;
  wbcondo_intervalos?: unknown;
  wbcondo_preco_clamper?: number | null;
  wallbox_itens?: unknown;
  wb_incluir_projeto_celesc?: boolean | null;
  wb_preco_projeto_celesc?: number | null;
  wb_qtd_projeto_celesc?: number | null;
  wb_incluir_projeto_cbmsc?: boolean | null;
  wb_preco_projeto_cbmsc?: number | null;
  wb_qtd_projeto_cbmsc?: number | null;
  wb_incluir_botao_emergencia?: boolean | null;
  wb_preco_botao_emergencia?: number | null;
  wb_qtd_botao_emergencia?: number | null;
  wb_incluir_pintura_sinalizacao?: boolean | null;
  wb_preco_pintura_sinalizacao?: number | null;
  wb_qtd_pintura_sinalizacao?: number | null;
  wb_incluir_sdai?: boolean | null;
  wb_preco_sdai?: number | null;
  wb_qtd_sdai?: number | null;
  wb_incluir_infraestrutura?: boolean | null;
  wb_preco_infraestrutura?: number | null;
  wb_qtd_infraestrutura?: number | null;
  wb_incluir_mao_obra?: boolean | null;
  wb_preco_mao_obra?: number | null;
  wb_qtd_mao_obra?: number | null;
  wb_incluir_quadro_transferencia?: boolean | null;
  wb_preco_quadro_transferencia?: number | null;
  wb_qtd_quadro_transferencia?: number | null;
  wb_incluir_clamper?: boolean | null;
  wb_preco_clamper?: number | null;
  wb_qtd_clamper?: number | null;
  wb_incluir_controladores_carga?: boolean | null;
  wb_preco_controladores_carga?: number | null;
  wb_qtd_controladores_carga?: number | null;
  leads: {
    id?: string;
    nome: string;
    tipo?: string;
    razao_social?: string;
    vendedor_id?: string;
    parceiro_id?: string;
    parceiro?: {
      id: string;
      nome: string;
    } | null;
  };
}

const CarregadoresVeiculares = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const { userProfile } = useAuthorization();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("leads");
  const [isAddLeadOpen, setIsAddLeadOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditingLead, setIsEditingLead] = useState(false);
  const [selectedLead, setSelectedLead] = useState<ExtendedLead | null>(null);
  const [generatedPdfUrl, setGeneratedPdfUrl] = useState<string | null>(null);
  const [isContractFormOpen, setIsContractFormOpen] = useState(false);
  const [selectedPropostaForContract, setSelectedPropostaForContract] = useState<PropostaCarregador | null>(null);
  const [sortColumn, setSortColumn] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filtroContratosEmAberto, setFiltroContratosEmAberto] = useState<boolean>(false);
  const [configHasUnsavedChanges, setConfigHasUnsavedChanges] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [pendingTab, setPendingTab] = useState<string | null>(null);

  // const blocker = useBlocker(
  //   ({ currentLocation, nextLocation }) =>
  //     !!configHasUnsavedChanges &&
  //     activeTab === "comissoes" &&
  //     currentLocation.pathname !== nextLocation.pathname
  // );

  const handleTabChange = useCallback((newValue: string) => {
    if (activeTab === "comissoes" && newValue !== "comissoes" && configHasUnsavedChanges) {
      setPendingTab(newValue);
      setShowLeaveConfirm(true);
    } else {
      setActiveTab(newValue);
    }
  }, [activeTab, configHasUnsavedChanges]);

  const handleConfirmLeave = useCallback(() => {
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
    setShowLeaveConfirm(false);
    // if (blocker?.state === "blocked" && typeof blocker.proceed === "function") {
    //   blocker.proceed();
    // }
  }, [pendingTab]);

  const handleCancelLeave = useCallback(() => {
    setPendingTab(null);
    setShowLeaveConfirm(false);
    // if (blocker?.state === "blocked" && typeof blocker.reset === "function") {
    //   blocker.reset();
    // }
  }, []);

  useEffect(() => {
    if (!configHasUnsavedChanges || activeTab !== "comissoes") return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [configHasUnsavedChanges, activeTab]);

  const isVendedor = userProfile?.role === 'vendedor';
  const isParceiro = userProfile?.role === 'parceiro';
  /** Parceiros apenas visualizam contratos. Apenas admin ou vendedor podem gerar, excluir, salvar ou alterar status. */
  const podeGerenciarContratos = userProfile?.role === 'admin' || userProfile?.role === 'vendedor';
  /** Parceiro que possui rede de parceiros (pode_criar_rede_parceiros) — pode ver leads/propostas da rede, sem editar */
  const isParceiroPai = isParceiro && (userProfile?.pode_criar_rede_parceiros === true);
  const isMobile = useIsMobile();

  /** True se o lead pertence a um parceiro da rede (não ao usuário logado). Parceiro pai só visualiza, não edita. */
  const isLeadFromRede = (lead: { parceiro_id?: string | null } | null) =>
    !!lead && isParceiroPai && lead.parceiro_id !== userProfile?.id;
  /** True se a proposta é de um lead de parceiro da rede. */
  const isPropostaFromRede = (proposta: { leads?: { parceiro_id?: string | null } | null } | null) =>
    !!proposta && isParceiroPai && proposta.leads?.parceiro_id !== userProfile?.id;

  // Função para obter o nome de exibição do lead (nome para PF, razão social para PJ)
  const getLeadDisplayName = (lead: any) => {
    if (!lead) return 'N/A';
    if (lead.tipo === 'juridica') {
      return lead.razao_social || lead.nome || 'Sem nome';
    }
    return lead.nome || 'Sem nome';
  };

  // Estados para o formulário
  const [incluirEquipamento, setIncluirEquipamento] = useState(false);
  const [incluirMateriaisWbcondo, setIncluirMateriaisWbcondo] = useState(false);
  const [formData, setFormData] = useState({
    lead_id: "",
    modelo_carregador: "Wallbox 7kW",
    potencia_kw: 7,
    preco_equipamento: 0,
    preco_instalacao: 1100,
    preco_materiais: 700,
    preco_projeto: 0,
    quantidade: 1,
    forma_pagamento: "pix",
    observacoes: "",
    valor_desconto: 0,
    desconto_observacao: "",
  });

  const defaultEvPropostaForm = () => ({
    qtdEstudos: 1,
    incluirTermografia: true,
    valorTermografia: 0,
    qtdTermografia: 1,
    incluirLaudoSpda: false,
    valorLaudoSpda: 0,
    qtdLaudoSpda: 1,
    incluirCompraAnalisador: false,
    valorCompraAnalisador: 0,
    qtdCompraAnalisador: 1,
    incluirAnaliseBombeiros: false,
    incluirManualTecnico: false,
    valorManualTecnico: 0,
    valorEstudo: 0,
  });
  const [evPropostaForm, setEvPropostaForm] = useState(defaultEvPropostaForm);
  /** Estudo EV: desconto só editável quando o switch está ativo; PDF sem linha de desconto se não houver valor. */
  const [incluirDescontoEstudoEv, setIncluirDescontoEstudoEv] = useState(false);

  const defaultSavePropostaForm = () => ({
    incluirAdequacoesEletricas: false,
    precoAdequacoesEletricas: 0,
    incluirAdequacoesIn23: false,
    precoAdequacoesIn23: 0,
    incluirOutrasAdequacoes: false,
    precoOutrasAdequacoes: 0,
    qtdUnidades: 1,
    pgtoEntradaTipo: "percentual" as SavePgtoTipo,
    pgtoEntradaValor: 40,
    pgtoInicioTipo: "percentual" as SavePgtoTipo,
    pgtoInicioValor: 30,
    pgtoConclusaoTipo: "percentual" as SavePgtoTipo,
    pgtoConclusaoValor: 30,
    parcEntrada: 0,
    parcNumParcelas: 10,
    parcJurosAm: 0,
    garantiaServicoMeses: SAVE_GARANTIA_SERVICO_MESES_PADRAO,
    garantiaClamperMeses: SAVE_GARANTIA_CLAMPER_MESES_PADRAO,
    cargaIncluirEstudo: false,
    cargaDemandaMaxima: 0,
    cargaDemandaMinima: 0,
    cargaDemandaMedia: 0,
    cargaDemandaRamal: 0,
    cargaQtdPontos: 1,
    cargaPotenciaPonto: SAVE_CARGA_POTENCIA_PADRAO_KW,
    cargaDemandaVerao: 0,
  });
  const [savePropostaForm, setSavePropostaForm] = useState(defaultSavePropostaForm);
  const [qtdUnidadesSaveInput, setQtdUnidadesSaveInput] = useState("1");

  const defaultPsavePropostaForm = () => ({
    precoCelesc: 0,
    precoCbmsc: 0,
  });
  const [psavePropostaForm, setPsavePropostaForm] = useState(defaultPsavePropostaForm);

  const defaultWbcondoPropostaForm = () => ({
    incluirIntervalos: false,
    intervalos: intervalosPadraoWallboxCondominio(),
  });
  const [wbcondoPropostaForm, setWbcondoPropostaForm] = useState(defaultWbcondoPropostaForm);
  const [wallboxItens, setWallboxItens] = useState<WallboxPropostaItem[]>(() => [
    itemPadraoWallboxProposta(),
  ]);
  const [wallboxExtrasForm, setWallboxExtrasForm] = useState(extrasPadraoWallboxProposta);

  const { data: valoresPropostasGlobaisLista } = useQuery({
    queryKey: ["valores-propostas-globais-lista"],
    queryFn: async () => {
      const { listarValoresPropostasGlobais } = await import("@/services/carregadoresValoresPropostasService");
      return listarValoresPropostasGlobais();
    },
  });
  const { data: modelosCatalogo } = useQuery({
    queryKey: ["modelos-carregadores-veiculares"],
    queryFn: () => modelosCarregadoresService.getAll(),
  });

  const modelosDisponiveis = Array.from(
    new Set([
      ...(valoresPropostasGlobaisLista?.map((v) => v.modelo_carregador) ?? []),
      ...(modelosCatalogo ?? []).map((modelo) => nomeExibicaoModeloCarregador(modelo)),
    ])
  );

  // Defaults de mão de obra e materiais por modelo para o usuário logado
  const { data: userCarregadorDefaults } = useQuery({
    queryKey: ["carregadores-user-defaults", userProfile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("carregadores_user_defaults")
        .select("modelo_carregador, preco_instalacao, preco_materiais");

      if (error) throw error;
      return data || [];
    },
    enabled: !!userProfile?.id,
  });

  // Helper para obter defaults de sistema (sem personalização) por modelo
  const getSystemDefaultsForModel = (modelo: string) => {
    const doCatalogo = modelosCatalogo?.find(
      (item) => nomeExibicaoModeloCarregador(item) === modelo
    );
    // Tentar extrair potência do nome do modelo (ex: "Wallbox 7kW" -> 7)
    const kwMatch = modelo.match(/(\d+(?:\.\d+)?)\s*kw/i);
    const kw = doCatalogo
      ? doCatalogo.potenciaNominalKw
      : kwMatch
        ? parseFloat(kwMatch[1])
        : (modelo.includes("7kW") ? 7 : modelo.includes("11kW") ? 11 : modelo.includes("22kW") ? 22 : 7);
    
    // Valores padrão baseados nos modelos padrão (fallback)
    let equip = 4500;
    let inst = 1100;
    let mat = 700;
    let projeto = 0;

    if (kw === 11 || modelo.includes("11kW")) {
      equip = 6500;
      inst = 1200;
      mat = 850;
    } else if (kw === 22 || modelo.includes("22kW")) {
      equip = 8500;
      inst = 1500;
      mat = 1000;
    } else if (kw !== 7 && !modelo.includes("7kW")) {
      // Para modelos customizados, usar valores padrão do 7kW como base
      equip = 4500;
      inst = 1100;
      mat = 700;
    }

    return { kw, equip, inst, mat, projeto };
  };

  // Buscar valores padrão de propostas por parceiro (todos os modelos)
  const { data: valoresPropostasParceiroLista } = useQuery({
    queryKey: ["valores-propostas-parceiro-lista", formData.lead_id],
    queryFn: async () => {
      if (!formData.lead_id) return [];

      const { data: lead, error } = await supabase
        .from("leads")
        .select("parceiro_id, produto")
        .eq("id", formData.lead_id)
        .maybeSingle();

      if (error || isProdutoEstudoViabilidadeEv(lead?.produto)) {
        return [];
      }
      if (!lead?.parceiro_id) return [];

      const { listarValoresPropostasParceiroPorParceiro } = await import(
        "@/services/carregadoresValoresPropostasService"
      );
      try {
        return await listarValoresPropostasParceiroPorParceiro(lead.parceiro_id);
      } catch {
        return [];
      }
    },
    enabled: !!formData.lead_id,
  });

  // Helper para obter defaults efetivos (considerando valores do parceiro ou globais)
  const getEffectiveDefaultsForModel = (modelo: string, _parceiroId?: string | null) => {
    const system = getSystemDefaultsForModel(modelo);
    const valoresParceiro = valoresPropostasParceiroLista?.find((v) => v.modelo_carregador === modelo);
    const valoresGlobais = valoresPropostasGlobaisLista?.find((v) => v.modelo_carregador === modelo);

    if (valoresParceiro) {
      return {
        potencia_kw: system.kw,
        preco_equipamento: incluirEquipamento ? valoresParceiro.preco_equipamento : 0,
        preco_instalacao: valoresParceiro.preco_instalacao,
        preco_materiais: valoresParceiro.preco_materiais,
        preco_projeto: system.projeto,
      };
    }

    if (valoresGlobais) {
      return {
        potencia_kw: system.kw,
        preco_equipamento: incluirEquipamento ? valoresGlobais.preco_equipamento : 0,
        preco_instalacao: valoresGlobais.preco_instalacao,
        preco_materiais: valoresGlobais.preco_materiais,
        preco_projeto: system.projeto,
      };
    }

    const userDefault = userCarregadorDefaults?.find(
      (d: { modelo_carregador: string }) => d.modelo_carregador === modelo
    );

    return {
      potencia_kw: system.kw,
      preco_equipamento: incluirEquipamento ? system.equip : 0,
      preco_instalacao: userDefault?.preco_instalacao ?? system.inst,
      preco_materiais: userDefault?.preco_materiais ?? system.mat,
      preco_projeto: system.projeto,
    };
  };

  const criarItemWallboxComDefaults = (modelo = "Wallbox 7kW"): WallboxPropostaItem => {
    const defaults = getEffectiveDefaultsForModel(modelo);
    return criarItemWallboxProposta(
      modelo,
      defaults.potencia_kw,
      1,
      incluirEquipamento ? defaults.preco_equipamento : 0
    );
  };

  // Funções de ação
  const updateStatus = async (id: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from("propostas_carregadores")
        .update({ status: newStatus })
        .eq("id", id);
      if (error) throw error;
      toast({ title: `Status atualizado para ${newStatus}` });
      queryClient.invalidateQueries({ queryKey: ["propostas-carregadores"] });
      // Invalidar também a query de propostas aceitas para atualizar a aba de contratos
      if (newStatus === 'aceita') {
        queryClient.invalidateQueries({ queryKey: ["propostas-aceitas-carregadores"] });
      }
    } catch (error) {
      toast({ title: "Erro ao atualizar status", variant: "destructive" });
    }
  };

  const handleOpenContractForm = (proposta: PropostaCarregador) => {
    setSelectedPropostaForContract(proposta);
    setIsContractFormOpen(true);
  };

  const gerarPDF = async (propostaId: string, detalhamentoEscopo?: string) => {
    setIsSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const detalhamentoTrim = detalhamentoEscopo?.trim() ?? "";
      const response = await fetchWithAuth('/api/propostas/carregadores/gerar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          propostaId,
          ...(detalhamentoTrim ? { detalhamentoEscopo: detalhamentoTrim } : {}),
        }),
        timeoutMs: 120000,
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => null);
        const detalhe =
          errBody && typeof errBody === "object"
            ? ("details" in errBody && typeof errBody.details === "string" && errBody.details.trim()
                ? errBody.details
                : "error" in errBody && typeof errBody.error === "string"
                  ? errBody.error
                  : null)
            : null;
        throw new Error(detalhe || "Falha ao gerar PDF");
      }
      
      const data = await response.json();
      setGeneratedPdfUrl(data.pdfUrl);
      toast({ title: "PDF gerado com sucesso!" });
      
      // Abrir o PDF em uma nova aba
      if (data.pdfUrl) {
        window.open(data.pdfUrl, '_blank');
      }
      
      queryClient.invalidateQueries({ queryKey: ["propostas-carregadores"] });
      return data.pdfUrl;
    } catch (error) {
      console.error(error);
      const description =
        error instanceof Error && error.message.trim()
          ? error.message
          : undefined;
      toast({
        title: "Erro ao gerar PDF",
        description,
        variant: "destructive",
      });
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para atualizar a lista de leads após adicionar um novo
  const fetchLeads = () => {
    queryClient.invalidateQueries({ queryKey: ["leads-carregadores-selection", userProfile?.id] });
  };

  // Busca leads para o select (apenas carregadores veiculares; parceiro pai vê também da rede)
  const { data: leads, isLoading: isLoadingLeads } = useQuery({
    queryKey: ["leads-carregadores-selection", userProfile?.id, isParceiroPai],
    queryFn: async () => {
      let query = supabase
        .from("leads")
        .select(`
          *,
          parceiro:parceiro_id (
            id,
            nome
          )
        `)
        .in("produto", [...PRODUTOS_LEAD_CARREGADOR]);
      
      if (isVendedor) {
        query = query.eq("vendedor_id", userProfile?.id);
      } else if (isParceiro && !isParceiroPai) {
        query = query.eq("parceiro_id", userProfile?.id);
      }
      // Parceiro pai (pode_criar_rede_parceiros): não filtra por parceiro_id; RLS retorna próprios + da rede
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!userProfile?.id
  });

  const selectedLeadForForm = useMemo(
    () => (leads as any[] | undefined)?.find((lead: any) => lead.id === formData.lead_id) ?? null,
    [leads, formData.lead_id]
  );
  const isEstudoEvLead = isProdutoEstudoViabilidadeEv(selectedLeadForForm?.produto);
  const isSaveLead = isProdutoImplantacaoSaveCondominios(selectedLeadForForm?.produto);
  const isProjetoAprovacaoSaveLead = isProdutoProjetoAprovacaoSave(selectedLeadForForm?.produto);
  const isWallboxCondominioLead = isProdutoInstalacaoWallboxCondominio(selectedLeadForForm?.produto);
  const isWallboxItensLead =
    !isEstudoEvLead && !isSaveLead && !isProjetoAprovacaoSaveLead;

  useEffect(() => {
    if (!isWallboxItensLead) return;
    const defaultsCompartilhados = getEffectiveDefaultsForModel("Wallbox 7kW");
    setFormData((prev) => ({
      ...prev,
      preco_instalacao: defaultsCompartilhados.preco_instalacao,
      preco_materiais: defaultsCompartilhados.preco_materiais,
      preco_projeto: defaultsCompartilhados.preco_projeto,
    }));
    setWallboxItens((prev) => {
      const source = prev.length > 0 ? prev : [itemPadraoWallboxProposta()];
      return source.map((item) => {
        const defaults = getEffectiveDefaultsForModel(item.modelo_carregador);
        return {
          ...item,
          potencia_kw: defaults.potencia_kw,
          preco_equipamento: incluirEquipamento ? defaults.preco_equipamento : 0,
        };
      });
    });
  }, [formData.lead_id, valoresPropostasParceiroLista, valoresPropostasGlobaisLista, isWallboxItensLead]);

  useEffect(() => {
    if (!isWallboxItensLead) return;
    setWallboxItens((prev) =>
      prev.map((item) => {
        const defaults = getEffectiveDefaultsForModel(item.modelo_carregador);
        return {
          ...item,
          preco_equipamento: incluirEquipamento ? defaults.preco_equipamento : 0,
        };
      })
    );
  }, [incluirEquipamento, isWallboxItensLead]);

  useEffect(() => {
    if (!isWallboxCondominioLead || !modelosCatalogo?.length) return;
    setWallboxItens((prev) => {
      const legado =
        prev.length === 1 &&
        !prev[0].modelo_id &&
        !modelosCatalogo.some(
          (modelo) => nomeExibicaoModeloCarregador(modelo) === prev[0].modelo_carregador
        );
      if (!legado) {
        let mudou = false;
        const atualizados = prev.map((item) => {
          const modelo = modelosCatalogo.find(
            (row) =>
              row.id === item.modelo_id ||
              nomeExibicaoModeloCarregador(row) === item.modelo_carregador
          );
          if (!modelo) return item;
          if (item.modelo_id === modelo.id && item.imagem_url === modelo.imagemUrl) return item;
          mudou = true;
          return {
            ...item,
            modelo_id: modelo.id,
            imagem_url: modelo.imagemUrl,
            modelo_carregador: nomeExibicaoModeloCarregador(modelo),
          };
        });
        return mudou ? atualizados : prev;
      }
      const primeiro = modelosCatalogo[0];
      const nome = nomeExibicaoModeloCarregador(primeiro);
      const defaults = getEffectiveDefaultsForModel(nome);
      return [
        criarItemWallboxProposta(
          nome,
          primeiro.potenciaNominalKw || defaults.potencia_kw,
          prev[0]?.quantidade || 1,
          incluirEquipamento ? defaults.preco_equipamento : 0,
          primeiro.id,
          primeiro.imagemUrl
        ),
      ];
    });
  }, [isWallboxCondominioLead, modelosCatalogo, formData.lead_id, incluirEquipamento]);

  useEffect(() => {
    if (!isSaveLead) return;
    setWallboxItens((prev) => {
      const source = prev.length > 0 ? prev : [itemPadraoWallboxProposta()];
      return source.map((item) => {
        const defaults = getEffectiveDefaultsForModel(item.modelo_carregador);
        return {
          ...item,
          potencia_kw: defaults.potencia_kw,
          preco_equipamento: incluirEquipamento ? defaults.preco_equipamento : 0,
        };
      });
    });
  }, [
    incluirEquipamento,
    isSaveLead,
    formData.lead_id,
    valoresPropostasParceiroLista,
    valoresPropostasGlobaisLista,
  ]);

  const prevPropostaLeadIdRef = useRef<string | null>(null);

  // Ao mudar o lead (ex.: botão "Criar proposta" na lista), alinhar modelo Wallbox vs Estudo EV vs SAVE
  useEffect(() => {
    if (!formData.lead_id) {
      prevPropostaLeadIdRef.current = null;
      return;
    }
    if (!leads?.length) return;
    const lead = (leads as any[]).find((l) => l.id === formData.lead_id);
    if (!lead) return;

    const leadIdChanged = prevPropostaLeadIdRef.current !== formData.lead_id;
    prevPropostaLeadIdRef.current = formData.lead_id;

    if (isProdutoEstudoViabilidadeEv(lead.produto)) {
      if (!leadIdChanged) return;
      setFormData((prev) => ({
        ...prev,
        modelo_carregador: PRODUTO_LEAD_ESTUDO_VIABILIDADE_EV,
        potencia_kw: 0,
        preco_equipamento: 0,
        preco_instalacao: 0,
        preco_materiais: 0,
        preco_projeto: 0,
        quantidade: 1,
        valor_desconto: 0,
        desconto_observacao: "",
      }));
      setIncluirEquipamento(false);
      setEvPropostaForm(defaultEvPropostaForm());
      setIncluirDescontoEstudoEv(false);
      setSavePropostaForm(defaultSavePropostaForm());
      setQtdUnidadesSaveInput("1");
      setPsavePropostaForm(defaultPsavePropostaForm());
      setWbcondoPropostaForm(defaultWbcondoPropostaForm());
      setWallboxItens([criarItemWallboxComDefaults()]);
      setWallboxExtrasForm(extrasPadraoWallboxProposta());
      return;
    }

    if (isProdutoImplantacaoSaveCondominios(lead.produto)) {
      if (!leadIdChanged) return;
      setFormData((prev) => ({
        ...prev,
        modelo_carregador: PRODUTO_LEAD_IMPLANTACAO_SAVE_CONDOMINIOS,
        potencia_kw: 0,
        preco_equipamento: 0,
        preco_instalacao: 0,
        preco_materiais: 0,
        preco_projeto: 0,
        quantidade: 1,
        valor_desconto: 0,
        desconto_observacao: "",
      }));
      setIncluirEquipamento(false);
      setEvPropostaForm(defaultEvPropostaForm());
      setIncluirDescontoEstudoEv(false);
      setSavePropostaForm(defaultSavePropostaForm());
      setQtdUnidadesSaveInput("1");
      setPsavePropostaForm(defaultPsavePropostaForm());
      setWbcondoPropostaForm(defaultWbcondoPropostaForm());
      setWallboxItens([criarItemWallboxComDefaults()]);
      setWallboxExtrasForm(extrasPadraoWallboxProposta());
      return;
    }

    if (isProdutoProjetoAprovacaoSave(lead.produto)) {
      if (!leadIdChanged) return;
      setFormData((prev) => ({
        ...prev,
        modelo_carregador: PRODUTO_LEAD_PROJETO_APROVACAO_SAVE,
        potencia_kw: 0,
        preco_equipamento: 0,
        preco_instalacao: 0,
        preco_materiais: 0,
        preco_projeto: 0,
        quantidade: 1,
        valor_desconto: 0,
        desconto_observacao: "",
      }));
      setIncluirEquipamento(false);
      setEvPropostaForm(defaultEvPropostaForm());
      setIncluirDescontoEstudoEv(false);
      setSavePropostaForm(defaultSavePropostaForm());
      setQtdUnidadesSaveInput("1");
      setPsavePropostaForm(defaultPsavePropostaForm());
      setWbcondoPropostaForm(defaultWbcondoPropostaForm());
      setWallboxItens([criarItemWallboxComDefaults()]);
      setWallboxExtrasForm(extrasPadraoWallboxProposta());
      return;
    }

    if (isProdutoInstalacaoWallboxCondominio(lead.produto)) {
      if (!leadIdChanged) return;
      setFormData((prev) => ({
        ...prev,
        modelo_carregador:
          prev.modelo_carregador === PRODUTO_LEAD_ESTUDO_VIABILIDADE_EV ||
          prev.modelo_carregador === PRODUTO_LEAD_IMPLANTACAO_SAVE_CONDOMINIOS ||
          prev.modelo_carregador === PRODUTO_LEAD_PROJETO_APROVACAO_SAVE
            ? "Wallbox 7kW"
            : prev.modelo_carregador,
        quantidade: 1,
        valor_desconto: 0,
        desconto_observacao: "",
      }));
      setEvPropostaForm(defaultEvPropostaForm());
      setIncluirDescontoEstudoEv(false);
      setSavePropostaForm(defaultSavePropostaForm());
      setQtdUnidadesSaveInput("1");
      setPsavePropostaForm(defaultPsavePropostaForm());
      setWbcondoPropostaForm(defaultWbcondoPropostaForm());
      const primeiroCatalogo = modelosCatalogo?.[0];
      if (primeiroCatalogo) {
        const nomeCatalogo = nomeExibicaoModeloCarregador(primeiroCatalogo);
        const defaultsCatalogo = getEffectiveDefaultsForModel(nomeCatalogo);
        setWallboxItens([
          criarItemWallboxProposta(
            nomeCatalogo,
            primeiroCatalogo.potenciaNominalKw || defaultsCatalogo.potencia_kw,
            1,
            incluirEquipamento ? defaultsCatalogo.preco_equipamento : 0,
            primeiroCatalogo.id,
            primeiroCatalogo.imagemUrl
          ),
        ]);
      } else {
        setWallboxItens([criarItemWallboxComDefaults()]);
      }
      setWallboxExtrasForm(extrasPadraoWallboxProposta());
      return;
    }

    if (!leadIdChanged) return;
    setFormData((prev) => {
      if (
        prev.modelo_carregador !== PRODUTO_LEAD_ESTUDO_VIABILIDADE_EV &&
        prev.modelo_carregador !== PRODUTO_LEAD_IMPLANTACAO_SAVE_CONDOMINIOS &&
        prev.modelo_carregador !== PRODUTO_LEAD_PROJETO_APROVACAO_SAVE
      ) {
        return prev;
      }
      return {
        ...prev,
        modelo_carregador: "Wallbox 7kW",
        potencia_kw: 7,
        quantidade: 1,
        valor_desconto: 0,
        desconto_observacao: "",
      };
    });
    setIncluirDescontoEstudoEv(false);
    setSavePropostaForm(defaultSavePropostaForm());
    setQtdUnidadesSaveInput("1");
    setPsavePropostaForm(defaultPsavePropostaForm());
    setWbcondoPropostaForm(defaultWbcondoPropostaForm());
    setWallboxItens([criarItemWallboxComDefaults()]);
    setWallboxExtrasForm(extrasPadraoWallboxProposta());
  }, [formData.lead_id, leads]);

  // Busca propostas (sem filtrar por created_by: vendedor/parceiro veem todas as propostas dos seus leads via RLS)
  const { data: propostas, isLoading } = useQuery({
    queryKey: ["propostas-carregadores", userProfile?.id],
    queryFn: async () => {
      const query = supabase.from("propostas_carregadores").select(`
          *,
          leads (
            id,
            nome,
            tipo,
            razao_social,
            vendedor_id,
            parceiro_id,
            parceiro:parceiro_id (
              id,
              nome
            )
          )
        `);

      const { data: propostasData, error } = await query.order("created_at", { ascending: false });
      if (error) throw error;

      // Buscar contratos para cada proposta
      if (propostasData && propostasData.length > 0) {
        const propostaIds = propostasData.map((p: any) => p.id);
        const { data: contratos, error: contratosError } = await supabase
          .from("contratos_carregadores")
          .select("id, proposta_id, contrato_pdf_url, status, created_at")
          .in("proposta_id", propostaIds);

        if (!contratosError && contratos) {
          return propostasData.map((proposta: any) =>
            anexarContratoPrincipalCarregador(proposta, contratos)
          );
        }
      }

      return propostasData as any[];
    },
    enabled: !!userProfile?.id
  });

  // Busca propostas aceitas (sem filtrar por created_by: vendedor/parceiro veem todas dos seus leads via RLS)
  const { data: propostasAceitas, isLoading: isLoadingContratos } = useQuery({
    queryKey: ["propostas-aceitas-carregadores", userProfile?.id],
    queryFn: async () => {
      const query = supabase
        .from("propostas_carregadores")
        .select(`
          *,
          leads (
            id,
            nome,
            tipo,
            razao_social,
            cnpj,
            cpf,
            representante_legal,
            cpf_representante,
            cep,
            endereco,
            numero,
            cidade,
            estado,
            telefone,
            email,
            vendedor_id,
            parceiro_id,
            parceiro:parceiro_id (
              id,
              nome
            )
          )
        `)
        .eq("status", "aceita");

      const { data: propostas, error } = await query.order("created_at", { ascending: false });
      if (error) throw error;

      // Buscar contratos para cada proposta
      if (propostas && propostas.length > 0) {
        const propostaIds = propostas.map((p: any) => p.id);
        const { data: contratos, error: contratosError } = await supabase
          .from("contratos_carregadores")
          .select("id, proposta_id, contrato_pdf_url, status, created_at")
          .in("proposta_id", propostaIds);

        if (!contratosError && contratos) {
          return propostas.map((proposta: any) =>
            anexarContratoPrincipalCarregador(proposta, contratos)
          );
        }
      }

      return propostas as any[];
    },
    enabled: !!userProfile?.id
  });

  const propostasAceitasFiltradas = useMemo(() => {
    if (!propostasAceitas) return [];
    if (!filtroContratosEmAberto) return propostasAceitas;
    return propostasAceitas.filter((p: any) => {
      const temContrato = p.contratos_carregadores?.length > 0 && p.contratos_carregadores[0].contrato_pdf_url;
      const status = p.contratos_carregadores?.[0]?.status;
      return !temContrato || isContratoEmAberto(status);
    });
  }, [propostasAceitas, filtroContratosEmAberto]);

  // Estados para rastrear propostas e contratos salvos
  const [salvandoProposta, setSalvandoProposta] = useState<string | null>(null);
  const [salvandoContrato, setSalvandoContrato] = useState<string | null>(null);
  const [propostasSalvas, setPropostasSalvas] = useState<Set<string>>(new Set());
  const [contratosSalvos, setContratosSalvos] = useState<Set<string>>(new Set());
  const [verificandoPropostas, setVerificandoPropostas] = useState(false);
  const [verificandoContratos, setVerificandoContratos] = useState(false);

  // Função para verificar se uma proposta já foi salva
  const verificarPropostaSalva = useCallback(async (leadId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return false;

      const response = await fetch(`/api/cliente-documentos/verificar-salvo/${leadId}?tipoDocumento=Proposta Comercial`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) return false;
      
      // Verificar se a resposta é JSON antes de fazer parse
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return false;
      }
      
      const data = await response.json();
      return data.jaSalvo;
    } catch (error) {
      console.error('Erro ao verificar proposta salva:', error);
      return false;
    }
  }, []);

  // Função para verificar se um contrato já foi salvo
  const verificarContratoSalvo = useCallback(async (leadId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return false;

      const response = await fetch(`/api/cliente-documentos/verificar-salvo/${leadId}?tipoDocumento=Contrato`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) return false;
      
      // Verificar se a resposta é JSON antes de fazer parse
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return false;
      }
      
      const data = await response.json();
      return data.jaSalvo;
    } catch (error) {
      console.error('Erro ao verificar contrato salvo:', error);
      return false;
    }
  }, []);

  // Verificar quais propostas já foram salvas
  useEffect(() => {
    const verificarPropostasSalvas = async () => {
      if (!propostas || propostas.length === 0) return;
      
      setVerificandoPropostas(true);
      const novasPropostasSalvas = new Set<string>();
      
      for (const proposta of propostas) {
        const leadId = proposta.leads?.id || proposta.lead_id;
        if (leadId) {
          const jaSalvo = await verificarPropostaSalva(leadId);
          if (jaSalvo) {
            novasPropostasSalvas.add(leadId);
          }
        }
      }
      
      setPropostasSalvas(novasPropostasSalvas);
      setVerificandoPropostas(false);
    };

    verificarPropostasSalvas();
  }, [propostas, verificarPropostaSalva]);

  // Verificar quais contratos já foram salvos
  useEffect(() => {
    const verificarContratosSalvos = async () => {
      if (!propostasAceitas || propostasAceitas.length === 0) return;
      
      setVerificandoContratos(true);
      const novosContratosSalvos = new Set<string>();
      
      for (const proposta of propostasAceitas) {
        const leadId = proposta.leads?.id || proposta.lead_id;
        if (leadId && proposta.contratos_carregadores && proposta.contratos_carregadores.length > 0 && proposta.contratos_carregadores[0].contrato_pdf_url) {
          const jaSalvo = await verificarContratoSalvo(leadId);
          if (jaSalvo) {
            novosContratosSalvos.add(leadId);
          }
        }
      }
      
      setContratosSalvos(novosContratosSalvos);
      setVerificandoContratos(false);
    };

    verificarContratosSalvos();
  }, [propostasAceitas, verificarContratoSalvo]);

  const quantidadeNumerica =
    typeof formData.quantidade === "number" && formData.quantidade > 0
      ? formData.quantidade
      : 1;

  // True quando o lead é de um parceiro (possui parceiro_id definido)
  const isLeadFromParceiro = !!selectedLeadForForm?.parceiro_id;

  // Admin pode editar mão de obra, materiais e projeto apenas quando a proposta não for de lead de parceiro
  const canEditValoresInstMateriais = userProfile?.role === "admin" && !isLeadFromParceiro;

  // Cálculo automático do total (Wallbox / EV / SAVE)
  const qtdEstudosEv = Math.max(1, Number(evPropostaForm.qtdEstudos) || 1);
  const qtdTermografiaEv = Math.max(1, Number(evPropostaForm.qtdTermografia) || 1);
  const qtdLaudoSpdaEv = Math.max(1, Number(evPropostaForm.qtdLaudoSpda) || 1);
  const qtdCompraAnalisadorEv = Math.max(1, Number(evPropostaForm.qtdCompraAnalisador) || 1);
  const qtdUnidadesSave = Math.max(1, Number(savePropostaForm.qtdUnidades) || 1);
  const valorSubtotalBruto = isEstudoEvLead
    ? Number(evPropostaForm.valorEstudo) * qtdEstudosEv +
      (evPropostaForm.incluirTermografia
        ? (Number(evPropostaForm.valorTermografia) || 0) * qtdTermografiaEv
        : 0) +
      (evPropostaForm.incluirLaudoSpda
        ? (Number(evPropostaForm.valorLaudoSpda) || 0) * qtdLaudoSpdaEv
        : 0) +
      (evPropostaForm.incluirCompraAnalisador
        ? (Number(evPropostaForm.valorCompraAnalisador) || 0) * qtdCompraAnalisadorEv
        : 0) +
      (evPropostaForm.incluirManualTecnico ? Number(evPropostaForm.valorManualTecnico) || 0 : 0)
    : isSaveLead
      ? (incluirEquipamento ? somaEquipamentoWallboxItens(wallboxItens) : 0) +
        somaExtrasWallboxProposta(wallboxExtrasForm) +
        (savePropostaForm.incluirAdequacoesEletricas
          ? Number(savePropostaForm.precoAdequacoesEletricas) || 0
          : 0) +
        (savePropostaForm.incluirAdequacoesIn23
          ? Number(savePropostaForm.precoAdequacoesIn23) || 0
          : 0) +
        (savePropostaForm.incluirOutrasAdequacoes
          ? Number(savePropostaForm.precoOutrasAdequacoes) || 0
          : 0)
      : isProjetoAprovacaoSaveLead
        ? (Number(psavePropostaForm.precoCelesc) || 0) +
          (Number(psavePropostaForm.precoCbmsc) || 0)
        : isWallboxCondominioLead
          ? somaEquipamentoWallboxItens(wallboxItens) +
            (incluirMateriaisWbcondo
              ? (Number(formData.preco_materiais) || 0) * somaQuantidadeWallboxItens(wallboxItens)
              : 0) +
            somaExtrasWallboxProposta(wallboxExtrasForm)
        : isWallboxItensLead
          ? subtotalPropostaWallboxCompartilhado(
              wallboxItens,
              {
                preco_instalacao: Number(formData.preco_instalacao) || 0,
                preco_materiais: Number(formData.preco_materiais) || 0,
              },
              somaExtrasWallboxProposta(wallboxExtrasForm)
            )
        : ((incluirEquipamento ? Number(formData.preco_equipamento) : 0) +
            Number(formData.preco_instalacao) +
            Number(formData.preco_materiais) +
            Number(formData.preco_projeto)) *
          quantidadeNumerica;

  const descontoBrutoForm = Math.max(0, Number(formData.valor_desconto) || 0);
  const descontoSolicitado =
    isEstudoEvLead && !incluirDescontoEstudoEv ? 0 : descontoBrutoForm;
  const descontoAplicado = Math.min(descontoSolicitado, valorSubtotalBruto);
  const valorTotal = Math.max(0, valorSubtotalBruto - descontoAplicado);
  const valorPorUnidadeSave = isSaveLead && qtdUnidadesSave > 0 ? valorTotal / qtdUnidadesSave : 0;
  const saveParcSaldo = Math.max(0, valorTotal - (Number(savePropostaForm.parcEntrada) || 0));
  const saveParcValorParcela = isSaveLead
    ? calcularParcelaTabelaPrice(
        saveParcSaldo,
        Number(savePropostaForm.parcJurosAm) || 0,
        Number(savePropostaForm.parcNumParcelas) || 1
      )
    : 0;
  const saveEtapasEntrada = {
    valor: savePropostaForm.pgtoEntradaValor,
    tipo: savePropostaForm.pgtoEntradaTipo,
  };
  const saveEtapasInicio = {
    valor: savePropostaForm.pgtoInicioValor,
    tipo: savePropostaForm.pgtoInicioTipo,
  };
  const saveEtapasConclusao = {
    valor: savePropostaForm.pgtoConclusaoValor,
    tipo: savePropostaForm.pgtoConclusaoTipo,
  };
  const saveSomaEtapas = isSaveLead
    ? saveSomaEtapasEmReais(
        saveEtapasEntrada,
        saveEtapasInicio,
        saveEtapasConclusao,
        valorTotal
      )
    : 0;
  const saveDiferencaEtapas = valorTotal - saveSomaEtapas;
  const saveEtapasOk = isSaveLead
    ? saveEtapasSomamTotal(
        saveEtapasEntrada,
        saveEtapasInicio,
        saveEtapasConclusao,
        valorTotal
      )
    : true;
  const psaveIncluiCelesc = (Number(psavePropostaForm.precoCelesc) || 0) > 0;
  const psaveIncluiCbmsc = (Number(psavePropostaForm.precoCbmsc) || 0) > 0;
  const psaveEscopoResumo =
    psaveIncluiCelesc && psaveIncluiCbmsc
      ? "Projeto Celesc + CBMSC"
      : psaveIncluiCelesc
        ? "Projeto Celesc"
        : psaveIncluiCbmsc
          ? "Projeto CBMSC"
          : "Projeto Celesc e/ou CBMSC";

  // Filtrar leads que ainda não possuem proposta
  const leadsSemProposta = leads?.filter(lead => 
    !propostas?.some(p => p.lead_id === lead.id)
  ) || [];

  // Função para ordenar leads
  const sortLeads = (leadsList: any[]) => {
    return [...leadsList].sort((a: any, b: any) => {
      if (!sortColumn) return 0;

      let aVal: string | number = '';
      let bVal: string | number = '';

      switch (sortColumn) {
        case 'nome':
          aVal = getLeadDisplayName(a);
          bVal = getLeadDisplayName(b);
          return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        case 'produto':
          aVal = a.produto || '';
          bVal = b.produto || '';
          return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        case 'telefone':
          aVal = a.telefone || '';
          bVal = b.telefone || '';
          return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        case 'email':
          aVal = a.email || '';
          bVal = b.email || '';
          return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        case 'parceiro':
          aVal = a.parceiro?.nome || '';
          bVal = b.parceiro?.nome || '';
          return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        case 'created_at': {
          const aDate = new Date(a.created_at || '1970-01-01');
          const bDate = new Date(b.created_at || '1970-01-01');
          return sortDirection === 'asc' ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
        }
        default:
          return 0;
      }
    });
  };

  // Leads que NÃO são clientes (não têm proposta aceita nem contrato gerado)
  const leadsPotenciais = leads?.filter(lead => {
    const propostasDoLead = propostas?.filter(p => p.lead_id === lead.id);
    const isCliente = propostasDoLead?.some(p => 
      p.status === 'aceita' || 
      (p.contratos_carregadores && p.contratos_carregadores.length > 0 && p.contratos_carregadores[0].contrato_pdf_url)
    );
    return !isCliente;
  }) || [];

  // Leads que SÃO clientes (têm proposta aceita ou contrato gerado)
  const leadsClientes = leads?.filter(lead => {
    const propostasDoLead = propostas?.filter(p => p.lead_id === lead.id);
    return propostasDoLead?.some(p =>
      p.status === 'aceita' ||
      (p.contratos_carregadores && p.contratos_carregadores.length > 0 && p.contratos_carregadores[0].contrato_pdf_url)
    );
  }) || [];

  const leadsPotenciaisFiltrados = leadsPotenciais.filter((lead: any) => {
    if (!appliedSearch) return true;
    const q = appliedSearch.toLowerCase();
    const name = (getLeadDisplayName(lead) || '').toLowerCase();
    const telefone = lead.telefone || '';
    const email = (lead.email || '').toLowerCase();
    const produto = (lead.produto || '').toLowerCase();
    const parceiro = (lead.parceiro?.nome || '').toLowerCase();
    return (
      name.includes(q) ||
      telefone.includes(appliedSearch) ||
      email.includes(q) ||
      produto.includes(q) ||
      parceiro.includes(q)
    );
  });

  const sortedLeadsPotenciais = sortLeads(leadsPotenciaisFiltrados);
  const sortedLeadsClientes = sortLeads(leadsClientes);

  // Função para mudar a ordenação
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.lead_id) {
      toast({ title: "Selecione um cliente", variant: "destructive" });
      return;
    }

    if (isEstudoEvLead) {
      if (Number(evPropostaForm.valorEstudo) < 0) {
        toast({ title: "Informe um valor válido para o estudo", variant: "destructive" });
        return;
      }
      if (!evPropostaForm.qtdEstudos || evPropostaForm.qtdEstudos < 1) {
        toast({
          title: "Quantidade de estudos",
          description: "Informe quantos estudos de viabilidade serão emitidos (mínimo 1).",
          variant: "destructive",
        });
        return;
      }
      if (evPropostaForm.incluirLaudoSpda) {
        if (Number(evPropostaForm.valorLaudoSpda) <= 0) {
          toast({
            title: "Laudo SPDA e aterramento",
            description: "Informe um valor maior que zero para o laudo SPDA e aterramento.",
            variant: "destructive",
          });
          return;
        }
        if (!evPropostaForm.qtdLaudoSpda || evPropostaForm.qtdLaudoSpda < 1) {
          toast({
            title: "Laudo SPDA e aterramento",
            description: "Informe quantidade mínima 1.",
            variant: "destructive",
          });
          return;
        }
      }
      if (evPropostaForm.incluirCompraAnalisador) {
        if (Number(evPropostaForm.valorCompraAnalisador) <= 0) {
          toast({
            title: "Analisador de Energia",
            description: "Informe um valor maior que zero para o analisador de energia.",
            variant: "destructive",
          });
          return;
        }
        if (!evPropostaForm.qtdCompraAnalisador || evPropostaForm.qtdCompraAnalisador < 1) {
          toast({
            title: "Analisador de Energia",
            description: "Informe quantidade mínima 1.",
            variant: "destructive",
          });
          return;
        }
      }
      if (evPropostaForm.incluirTermografia) {
        if (Number(evPropostaForm.valorTermografia) <= 0) {
          toast({
            title: "Termografia",
            description: "Informe o valor da análise termográfica ou desative a opção.",
            variant: "destructive",
          });
          return;
        }
        if (!evPropostaForm.qtdTermografia || evPropostaForm.qtdTermografia < 1) {
          toast({
            title: "Termografia",
            description: "Informe a quantidade de análises termográficas (mínimo 1).",
            variant: "destructive",
          });
          return;
        }
      }
      if (evPropostaForm.incluirManualTecnico) {
        if (Number(evPropostaForm.valorManualTecnico) <= 0) {
          toast({
            title: "Manual técnico",
            description: "Informe o valor do manual técnico de instalação e diretrizes.",
            variant: "destructive",
          });
          return;
        }
      }
    }

    if (isSaveLead) {
      const erroExtrasSave = validarExtrasWallboxProposta(wallboxExtrasForm);
      if (erroExtrasSave) {
        toast({
          title: "Itens complementares",
          description: erroExtrasSave,
          variant: "destructive",
        });
        return;
      }
      if (incluirEquipamento) {
        const erroItensSave = validarWallboxItensProposta(wallboxItens);
        if (erroItensSave) {
          toast({
            title: "Carregadores da proposta",
            description: erroItensSave,
            variant: "destructive",
          });
          return;
        }
        if (somaEquipamentoWallboxItens(wallboxItens) <= 0) {
          toast({
            title: "Carregadores da proposta",
            description:
              "Informe o preço do equipamento ou desative a inclusão dos carregadores.",
            variant: "destructive",
          });
          return;
        }
      }
      if (
        savePropostaForm.incluirAdequacoesEletricas &&
        (Number(savePropostaForm.precoAdequacoesEletricas) || 0) <= 0
      ) {
        toast({
          title: "Adequações elétricas",
          description: "Informe um valor maior que zero ou desative a opção.",
          variant: "destructive",
        });
        return;
      }
      if (
        savePropostaForm.incluirAdequacoesIn23 &&
        (Number(savePropostaForm.precoAdequacoesIn23) || 0) <= 0
      ) {
        toast({
          title: "Adequações IN-23",
          description: "Informe um valor maior que zero ou desative a opção.",
          variant: "destructive",
        });
        return;
      }
      if (
        savePropostaForm.incluirOutrasAdequacoes &&
        (Number(savePropostaForm.precoOutrasAdequacoes) || 0) <= 0
      ) {
        toast({
          title: "Outras adequações",
          description: "Informe um valor maior que zero ou desative a opção.",
          variant: "destructive",
        });
        return;
      }
      if (savePropostaForm.cargaIncluirEstudo) {
        const erroCarga = validarSaveCargaEstudo({
          demandaMaximaKw: Number(savePropostaForm.cargaDemandaMaxima) || 0,
          demandaMinimaKw: Number(savePropostaForm.cargaDemandaMinima) || 0,
          demandaMediaKw: Number(savePropostaForm.cargaDemandaMedia) || 0,
          demandaRamalKw: Number(savePropostaForm.cargaDemandaRamal) || 0,
          qtdPontos: Math.max(1, Number(savePropostaForm.cargaQtdPontos) || 1),
          potenciaPontoKw:
            Number(savePropostaForm.cargaPotenciaPonto) || SAVE_CARGA_POTENCIA_PADRAO_KW,
          demandaVeraoKw: Number(savePropostaForm.cargaDemandaVerao) || 0,
        });
        if (erroCarga) {
          toast({
            title: "Análise de carga",
            description: erroCarga,
            variant: "destructive",
          });
          return;
        }
      }
      if (wbcondoPropostaForm.incluirIntervalos) {
        const erroIntervalosSave = validarIntervalosWallboxCondominio(
          wbcondoPropostaForm.intervalos
        );
        if (erroIntervalosSave) {
          toast({
            title: "Intervalos de distância",
            description: erroIntervalosSave,
            variant: "destructive",
          });
          return;
        }
      }
      if (valorTotal <= 0) {
        toast({
          title: "Valores do projeto",
          description: "Informe ao menos um item de custo com valor maior que zero.",
          variant: "destructive",
        });
        return;
      }
      if (!savePropostaForm.qtdUnidades || savePropostaForm.qtdUnidades < 1) {
        toast({
          title: "Unidades do condomínio",
          description: "Informe o número de unidades a serem atendidas (mínimo 1).",
          variant: "destructive",
        });
        return;
      }
      if (!saveEtapasOk) {
        toast({
          title: "Formas de pagamento — etapas",
          description: "A soma de Entrada + Início obra + Conclusão deve ser igual ao valor total.",
          variant: "destructive",
        });
        return;
      }
      if ((Number(savePropostaForm.parcEntrada) || 0) > valorTotal) {
        toast({
          title: "Parcelamento",
          description: "A entrada do parcelamento não pode ser maior que o valor total.",
          variant: "destructive",
        });
        return;
      }
      const nParc = Number(savePropostaForm.parcNumParcelas) || 0;
      if (nParc < 1 || nParc > 10) {
        toast({
          title: "Parcelamento",
          description: "Informe de 1 a 10 parcelas.",
          variant: "destructive",
        });
        return;
      }
      if ((Number(savePropostaForm.garantiaServicoMeses) || 0) < 1) {
        toast({
          title: "Garantias",
          description: "Informe a garantia dos serviços em meses (mínimo 1).",
          variant: "destructive",
        });
        return;
      }
      if ((Number(savePropostaForm.garantiaClamperMeses) || 0) < 1) {
        toast({
          title: "Garantias",
          description: "Informe a garantia dos materiais Clamper em meses (mínimo 1).",
          variant: "destructive",
        });
        return;
      }
    }

    if (isProjetoAprovacaoSaveLead) {
      const precoCelescPsave = Number(psavePropostaForm.precoCelesc) || 0;
      const precoCbmscPsave = Number(psavePropostaForm.precoCbmsc) || 0;
      if (precoCelescPsave <= 0 && precoCbmscPsave <= 0) {
        toast({
          title: "Valores do projeto",
          description:
            "Informe o valor de ao menos um dos projetos (Celesc ou CBMSC). Deixe em R$ 0,00 o que não for contratado.",
          variant: "destructive",
        });
        return;
      }
      if (valorTotal <= 0) {
        toast({
          title: "Valor total",
          description: "O valor total da proposta deve ser maior que zero.",
          variant: "destructive",
        });
        return;
      }
    }

    if (isWallboxCondominioLead && incluirMateriaisWbcondo && (Number(formData.preco_materiais) || 0) <= 0) {
      toast({
        title: "Materiais",
        description: "Informe o valor dos materiais ou desative a opção.",
        variant: "destructive",
      });
      return;
    }

    if (isWallboxCondominioLead && wbcondoPropostaForm.incluirIntervalos) {
      const erroIntervalos = validarIntervalosWallboxCondominio(wbcondoPropostaForm.intervalos);
      if (erroIntervalos) {
        toast({
          title: "Intervalos de distância",
          description: erroIntervalos,
          variant: "destructive",
        });
        return;
      }
    }

    if (isWallboxItensLead) {
      if (isWallboxCondominioLead && wallboxItens.some((item) => !item.modelo_id)) {
        toast({
          title: "Modelo do carregador",
          description: "Selecione um modelo do Catálogo de Carregadores para incluir na proposta.",
          variant: "destructive",
        });
        return;
      }
      const erroItens = validarWallboxItensProposta(wallboxItens);
      if (erroItens) {
        toast({
          title: "Carregadores da proposta",
          description: erroItens,
          variant: "destructive",
        });
        return;
      }
      const erroExtras = validarExtrasWallboxProposta(wallboxExtrasForm);
      if (erroExtras) {
        toast({
          title: "Itens complementares",
          description: erroExtras,
          variant: "destructive",
        });
        return;
      }
    }

    setIsSubmitting(true);
    setGeneratedPdfUrl(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const baseRow = {
        lead_id: formData.lead_id,
        forma_pagamento: formData.forma_pagamento,
        observacoes: formData.observacoes.trim() || null,
        valor_total: valorTotal,
        valor_desconto: descontoAplicado,
        desconto_observacao:
          descontoAplicado > 0
            ? formData.desconto_observacao.trim().slice(0, 500)
            : "",
        created_by: user?.id,
        status: "enviada" as const,
        quantidade: isEstudoEvLead
          ? qtdEstudosEv
          : isSaveLead
            ? qtdUnidadesSave
            : isWallboxItensLead
              ? agregarWallboxItensParaColunas(wallboxItens).quantidade
              : formData.quantidade,
      };

      const insertPayload = isEstudoEvLead
        ? {
            ...baseRow,
            modelo_carregador: PRODUTO_LEAD_ESTUDO_VIABILIDADE_EV,
            potencia_kw: 0,
            preco_equipamento: 0,
            preco_instalacao: 0,
            preco_materiais: 0,
            preco_projeto: 0,
            ev_qtd_estudos: qtdEstudosEv,
            ev_incluir_termografia: evPropostaForm.incluirTermografia,
            ev_valor_termografia: evPropostaForm.incluirTermografia
              ? Number(evPropostaForm.valorTermografia)
              : 0,
            ev_qtd_termografia: evPropostaForm.incluirTermografia ? qtdTermografiaEv : 1,
            ev_incluir_laudo_spda: evPropostaForm.incluirLaudoSpda,
            ev_valor_laudo_spda: evPropostaForm.incluirLaudoSpda
              ? Number(evPropostaForm.valorLaudoSpda)
              : 0,
            ev_qtd_laudo_spda: evPropostaForm.incluirLaudoSpda ? qtdLaudoSpdaEv : 1,
            ev_incluir_compra_analisador: evPropostaForm.incluirCompraAnalisador,
            ev_valor_compra_analisador: evPropostaForm.incluirCompraAnalisador
              ? Number(evPropostaForm.valorCompraAnalisador)
              : 0,
            ev_qtd_compra_analisador: evPropostaForm.incluirCompraAnalisador
              ? qtdCompraAnalisadorEv
              : 1,
            ev_incluir_analise_bombeiros: evPropostaForm.incluirAnaliseBombeiros,
            ev_incluir_projeto_eletrico: evPropostaForm.incluirManualTecnico,
            ev_valor_projeto_eletrico: evPropostaForm.incluirManualTecnico
              ? Number(evPropostaForm.valorManualTecnico)
              : 0,
            ev_qtd_carregadores_projeto: null,
            ev_valor_estudo: Number(evPropostaForm.valorEstudo),
          }
        : isSaveLead
          ? {
              ...baseRow,
              modelo_carregador: PRODUTO_LEAD_IMPLANTACAO_SAVE_CONDOMINIOS,
              potencia_kw: 0,
              preco_equipamento: incluirEquipamento
                ? agregarWallboxItensParaColunas(wallboxItens).preco_equipamento
                : 0,
              preco_instalacao: 0,
              wallbox_itens: incluirEquipamento
                ? serializarWallboxItensProposta(wallboxItens)
                : [],
              preco_materiais: 0,
              preco_projeto: 0,
              save_preco_projeto_aprovacao:
                subtotalExtraWallbox(wallboxExtrasForm.projeto_celesc) +
                subtotalExtraWallbox(wallboxExtrasForm.projeto_cbmsc),
              save_preco_adequacoes_eletricas: savePropostaForm.incluirAdequacoesEletricas
                ? Number(savePropostaForm.precoAdequacoesEletricas) || 0
                : 0,
              save_preco_adequacoes_in23: savePropostaForm.incluirAdequacoesIn23
                ? Number(savePropostaForm.precoAdequacoesIn23) || 0
                : 0,
              save_preco_outras_adequacoes: savePropostaForm.incluirOutrasAdequacoes
                ? Number(savePropostaForm.precoOutrasAdequacoes) || 0
                : 0,
              save_qtd_unidades: qtdUnidadesSave,
              save_pgto_entrada_tipo: savePropostaForm.pgtoEntradaTipo,
              save_pgto_entrada_valor: Number(savePropostaForm.pgtoEntradaValor) || 0,
              save_pgto_inicio_tipo: savePropostaForm.pgtoInicioTipo,
              save_pgto_inicio_valor: Number(savePropostaForm.pgtoInicioValor) || 0,
              save_pgto_conclusao_tipo: savePropostaForm.pgtoConclusaoTipo,
              save_pgto_conclusao_valor: Number(savePropostaForm.pgtoConclusaoValor) || 0,
              save_parc_entrada: Number(savePropostaForm.parcEntrada) || 0,
              save_parc_num_parcelas: Number(savePropostaForm.parcNumParcelas) || 1,
              save_parc_juros_am: Number(savePropostaForm.parcJurosAm) || 0,
              save_parc_valor_parcela: Math.round(saveParcValorParcela * 100) / 100,
              save_garantia_servico_meses: parseMesesGarantiaSave(
                savePropostaForm.garantiaServicoMeses,
                SAVE_GARANTIA_SERVICO_MESES_PADRAO
              ),
              save_garantia_clamper_meses: parseMesesGarantiaSave(
                savePropostaForm.garantiaClamperMeses,
                SAVE_GARANTIA_CLAMPER_MESES_PADRAO
              ),
              save_carga_modo: savePropostaForm.cargaIncluirEstudo ? "estudo" : "exemplo",
              save_carga_demanda_maxima_kw: savePropostaForm.cargaIncluirEstudo
                ? Number(savePropostaForm.cargaDemandaMaxima) || 0
                : 0,
              save_carga_demanda_minima_kw: savePropostaForm.cargaIncluirEstudo
                ? Number(savePropostaForm.cargaDemandaMinima) || 0
                : 0,
              save_carga_demanda_media_kw: savePropostaForm.cargaIncluirEstudo
                ? Number(savePropostaForm.cargaDemandaMedia) || 0
                : 0,
              save_carga_demanda_ramal_kw: savePropostaForm.cargaIncluirEstudo
                ? Number(savePropostaForm.cargaDemandaRamal) || 0
                : 0,
              save_carga_qtd_pontos: savePropostaForm.cargaIncluirEstudo
                ? Math.max(1, Number(savePropostaForm.cargaQtdPontos) || 1)
                : 1,
              save_carga_potencia_ponto_kw: savePropostaForm.cargaIncluirEstudo
                ? Number(savePropostaForm.cargaPotenciaPonto) || SAVE_CARGA_POTENCIA_PADRAO_KW
                : SAVE_CARGA_POTENCIA_PADRAO_KW,
              save_carga_demanda_verao_kw: savePropostaForm.cargaIncluirEstudo
                ? Number(savePropostaForm.cargaDemandaVerao) || 0
                : 0,
              ...serializarExtrasWallboxProposta(wallboxExtrasForm),
              wbcondo_intervalos: wbcondoPropostaForm.incluirIntervalos
                ? wbcondoPropostaForm.intervalos
                : [],
              wbcondo_preco_clamper: wallboxExtrasForm.clamper.incluir
                ? subtotalExtraWallbox(wallboxExtrasForm.clamper)
                : 0,
            }
          : isProjetoAprovacaoSaveLead
            ? {
                ...baseRow,
                modelo_carregador: PRODUTO_LEAD_PROJETO_APROVACAO_SAVE,
                potencia_kw: 0,
                preco_equipamento: 0,
                preco_instalacao: 0,
                preco_materiais: 0,
                preco_projeto: 0,
                psave_preco_celesc: Number(psavePropostaForm.precoCelesc) || 0,
                psave_preco_cbmsc: Number(psavePropostaForm.precoCbmsc) || 0,
              }
          : (() => {
              const colunas = agregarWallboxItensParaColunas(wallboxItens);
              const extrasColunas = serializarExtrasWallboxProposta(wallboxExtrasForm);
              return {
                ...formData,
                ...baseRow,
                modelo_carregador: colunas.modelo_carregador,
                potencia_kw: colunas.potencia_kw,
                quantidade: colunas.quantidade,
                preco_equipamento: incluirEquipamento ? colunas.preco_equipamento : 0,
                preco_instalacao: isWallboxCondominioLead
                  ? 0
                  : Number(formData.preco_instalacao) || 0,
                preco_materiais: isWallboxCondominioLead
                  ? incluirMateriaisWbcondo
                    ? Number(formData.preco_materiais) || 0
                    : 0
                  : Number(formData.preco_materiais) || 0,
                preco_projeto: 0,
                wallbox_itens: serializarWallboxItensProposta(wallboxItens),
                ...extrasColunas,
                ...(isWallboxCondominioLead
                  ? {
                      wbcondo_intervalos: wbcondoPropostaForm.incluirIntervalos
                        ? wbcondoPropostaForm.intervalos
                        : [],
                      wbcondo_preco_clamper: extrasColunas.wb_incluir_clamper
                        ? extrasColunas.wb_preco_clamper * extrasColunas.wb_qtd_clamper
                        : 0,
                    }
                  : {}),
              };
            })();

      const { data: newProposta, error } = await supabase
        .from("propostas_carregadores")
        .insert(insertPayload as any)
        .select()
        .single();

      if (error) {
        // Já existe proposta para este lead (constraint UNIQUE lead_id)
        if (error.code === "23505") {
          toast({
            title: "Proposta já existe",
            description: "Este lead já possui uma proposta. Atualize a página para visualizá-la.",
            variant: "destructive",
          });
          queryClient.invalidateQueries({ queryKey: ["propostas-carregadores"] });
          return;
        }
        throw error;
      }

      toast({
        title:
          isEstudoEvLead || isSaveLead || isProjetoAprovacaoSaveLead
            ? "Proposta salva! Gerando PDF..."
            : "Proposta criada com sucesso! Gerando PDF...",
      });
      if (newProposta) {
        await gerarPDF(newProposta.id, formData.observacoes);
      }

      queryClient.invalidateQueries({ queryKey: ["propostas-carregadores"] });

      const effectiveDefaults = getEffectiveDefaultsForModel("Wallbox 7kW");

      setIncluirEquipamento(false);
      setIncluirMateriaisWbcondo(false);
      setEvPropostaForm(defaultEvPropostaForm());
      setSavePropostaForm(defaultSavePropostaForm());
      setQtdUnidadesSaveInput("1");
      setPsavePropostaForm(defaultPsavePropostaForm());
      setWbcondoPropostaForm(defaultWbcondoPropostaForm());
      setWallboxItens([criarItemWallboxProposta("Wallbox 7kW", 7, 1, 0)]);
      setWallboxExtrasForm(extrasPadraoWallboxProposta());
      setIncluirDescontoEstudoEv(false);
      setFormData({
        lead_id: "",
        modelo_carregador: "Wallbox 7kW",
        potencia_kw: effectiveDefaults.potencia_kw,
        preco_equipamento: 0,
        preco_instalacao: effectiveDefaults.preco_instalacao,
        preco_materiais: effectiveDefaults.preco_materiais,
        preco_projeto: effectiveDefaults.preco_projeto,
        quantidade: 1,
        forma_pagamento: "pix",
        observacoes: "",
        valor_desconto: 0,
        desconto_observacao: "",
      });
    } catch (error: unknown) {
      console.error(error);
      const description =
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof (error as { message?: unknown }).message === "string"
          ? (error as { message: string }).message
          : undefined;
      toast({
        title: "Erro ao criar proposta",
        description: description ?? "Tente novamente ou verifique se o banco está atualizado.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteMutation = useMutation({
    mutationFn: async ({ id, cascade = false }: { id: string, cascade?: boolean; leadId?: string }) => {
      console.log('🗑️ Tentando excluir proposta:', id, 'cascade:', cascade);
      
      const { data: { session } } = await supabase.auth.getSession();
      const url = `/api/propostas/carregadores/${id}${cascade ? '?cascade=true' : ''}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw { ...errorData, status: response.status };
      }

      const result = await response.json();
      console.log('✅ Proposta excluída com sucesso:', result);
      return result;
    },
    onSuccess: (_data, variables) => {
      toast({ title: "Proposta excluída com sucesso" });
      queryClient.invalidateQueries({ queryKey: ["propostas-carregadores"] });
      queryClient.invalidateQueries({ queryKey: ["propostas-aceitas-carregadores"] });
      queryClient.invalidateQueries({ queryKey: ["leads-carregadores-selection", userProfile?.id] });
      if (variables.leadId) {
        queryClient.invalidateQueries({ queryKey: [CLIENTE_DOCUMENTOS_QUERY_KEY, variables.leadId] });
      }
    },
    onError: (error: any) => {
      console.error('❌ Erro na mutation:', error);
      const errorMessage = error?.error || error?.message || 'Erro desconhecido ao excluir proposta';
      toast({ 
        title: "Erro ao excluir proposta", 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  const deleteContratoMutation = useMutation({
    mutationFn: async (contratoId: string) => {
      console.log('🗑️ Tentando excluir contrato:', contratoId);
      
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`/api/contratos-carregadores/${contratoId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `Erro ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Contrato excluído com sucesso:', result);
      return result;
    },
    onSuccess: (result: { leadRevertido?: boolean }) => {
      toast({
        title: "Contrato excluído com sucesso",
        description: result?.leadRevertido
          ? "O cliente voltou a ser lead na etapa Análise de Necessidades."
          : undefined,
      });
      queryClient.invalidateQueries({ queryKey: ["propostas-aceitas-carregadores"] });
      queryClient.invalidateQueries({ queryKey: ["propostas-carregadores"] });
    },
    onError: (error: any) => {
      console.error('❌ Erro na mutation:', error);
      const errorMessage = error?.message || 'Erro desconhecido ao excluir contrato';
      toast({ 
        title: "Erro ao excluir contrato", 
        description: errorMessage,
        variant: "destructive" 
      });
    },
  });

  const updateContratoStatusMutation = useMutation({
    mutationFn: async ({ contratoId, status }: { contratoId: string; status: string }) => {
      const response = await fetchWithAuth(`/api/contratos-carregadores/${contratoId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.details || `Erro ${response.status}`);
      }
      return response.json();
    },
    onSuccess: (_, { status }) => {
      toast({ title: "Status atualizado", description: getContratoStatusLabel(status) });
      queryClient.invalidateQueries({ queryKey: ["propostas-aceitas-carregadores"] });
      queryClient.invalidateQueries({ queryKey: ["propostas-carregadores"] });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar status",
        description: error?.message || "Tente novamente",
        variant: "destructive",
      });
    },
  });

  const handleSalvarContrato = async (
    contratoPdfUrl: string,
    leadId: string,
    nomeCliente?: string,
    isEstudoViabilidadeEv?: boolean
  ) => {
    if (!contratoPdfUrl) {
      toast({ 
        title: "Erro", 
        description: "O contrato ainda não possui PDF gerado.",
        variant: "destructive" 
      });
      return;
    }

    if (!leadId) {
      toast({ 
        title: "Erro", 
        description: "Não foi possível identificar o cliente do contrato.",
        variant: "destructive" 
      });
      return;
    }

    setSalvandoContrato(leadId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Usuário não autenticado');
      }

      const prefixoContrato = isEstudoViabilidadeEv ? "Contrato_Estudo_Viabilidade_EV" : "Contrato_Carregador";
      const nomeArquivo = `${prefixoContrato}_${nomeCliente?.replace(/[^a-zA-Z0-9]/g, "_") || "Cliente"}`;

      const response = await fetch('/api/cliente-documentos/salvar-proposta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          leadId: leadId,
          propostaPdfUrl: contratoPdfUrl,
          nomeArquivo: nomeArquivo,
          tipoDocumento: 'Contrato'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao salvar contrato');
      }

      // Marcar contrato como salvo e atualizar a aba Documentos do cliente (se estiver aberta)
      setContratosSalvos(prev => new Set([...prev, leadId]));
      queryClient.invalidateQueries({ queryKey: [CLIENTE_DOCUMENTOS_QUERY_KEY, leadId] });

      toast({ 
        title: "Sucesso", 
        description: "Contrato salvo na aba documentos do cliente com sucesso!" 
      });
    } catch (error) {
      console.error('Erro ao salvar contrato:', error);
      toast({ 
        title: "Erro", 
        description: error instanceof Error ? error.message : 'Erro ao salvar contrato como documento',
        variant: "destructive" 
      });
    } finally {
      setSalvandoContrato(null);
    }
  };

  const handleSalvarProposta = async (proposta: PropostaCarregador) => {
    if (!proposta.proposta_pdf_url) {
      toast({ 
        title: "Erro", 
        description: "A proposta ainda não possui PDF gerado. Gere o PDF primeiro.",
        variant: "destructive" 
      });
      return;
    }

    const leadId = proposta.leads?.id || proposta.lead_id;
    
    if (!leadId) {
      toast({ 
        title: "Erro", 
        description: "Não foi possível identificar o cliente da proposta.",
        variant: "destructive" 
      });
      return;
    }

    setSalvandoProposta(proposta.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Usuário não autenticado');
      }

      const prefixoProposta =
        proposta.modelo_carregador === PRODUTO_LEAD_ESTUDO_VIABILIDADE_EV
          ? 'Proposta_Estudo_EV'
          : 'Proposta_Carregador';
      const nomeArquivo = `${prefixoProposta}_${getLeadDisplayName(proposta.leads).replace(/[^a-zA-Z0-9]/g, '_')}`;

      const response = await fetch('/api/cliente-documentos/salvar-proposta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          leadId: leadId,
          propostaPdfUrl: proposta.proposta_pdf_url,
          nomeArquivo: nomeArquivo
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao salvar proposta');
      }

      // Marcar proposta como salva e atualizar a aba Documentos do cliente (se estiver aberta)
      setPropostasSalvas(prev => new Set([...prev, leadId]));
      queryClient.invalidateQueries({ queryKey: [CLIENTE_DOCUMENTOS_QUERY_KEY, leadId] });

      toast({ 
        title: "Sucesso", 
        description: "Proposta salva na aba documentos do cliente com sucesso!" 
      });
    } catch (error) {
      console.error('Erro ao salvar proposta:', error);
      toast({ 
        title: "Erro", 
        description: error instanceof Error ? error.message : 'Erro ao salvar proposta como documento',
        variant: "destructive" 
      });
    } finally {
      setSalvandoProposta(null);
    }
  };

  const handleBaixarContrato = async (
    contratoPdfUrl: string,
    nomeCliente?: string,
    isEstudoViabilidadeEv?: boolean
  ) => {
    if (contratoPdfUrl) {
      try {
        const response = await fetch(contratoPdfUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Extrair o primeiro nome do cliente
        let primeiroNome = '';
        if (nomeCliente) {
          primeiroNome = nomeCliente.split(' ')[0].replace(/[^a-zA-Z0-9]/g, '_');
        }
        
        const prefixo = isEstudoViabilidadeEv ? "Contrato_Estudo_Viabilidade_EV" : "Contrato_Carregador_Veicular";
        const nomeArquivo = primeiroNome
          ? `${prefixo}_${primeiroNome}.pdf`
          : `${prefixo}_${Date.now()}.pdf`;
        
        a.download = nomeArquivo;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast({ title: "Contrato baixado com sucesso" });
      } catch (error) {
        console.error('Erro ao baixar contrato:', error);
        toast({ title: "Erro ao baixar contrato", variant: "destructive" });
      }
    } else {
      toast({ title: "URL do contrato não encontrada", variant: "destructive" });
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Carregadores Veiculares</h1>
        <p className="text-muted-foreground">Gestão de precificação e propostas de instalação</p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="flex w-full flex-wrap justify-start gap-1 h-auto min-h-[2.5rem]">
          <TabsTrigger value="leads" className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0 sm:flex-initial">Leads Cadastrados</TabsTrigger>
          <TabsTrigger value="new" className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0 sm:flex-initial">Gerar Nova Proposta</TabsTrigger>
          <TabsTrigger value="list" className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0 sm:flex-initial">Propostas</TabsTrigger>
          <TabsTrigger value="contratos" className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0 sm:flex-initial">Contratos</TabsTrigger>
          <TabsTrigger value="clientes" className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0 sm:flex-initial">Clientes</TabsTrigger>
          {(userProfile?.role === "admin" || userProfile?.role === "parceiro") && (
            <TabsTrigger value="comissoes" className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0 sm:flex-initial">
              {userProfile?.role === "parceiro" ? "Comissões" : "Configurações"}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="leads" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
                <div>
                  <CardTitle>Leads de Carregadores</CardTitle>
                  <CardDescription>Lista de potenciais clientes interessados em carregadores veiculares.</CardDescription>
                </div>
                <Dialog open={isAddLeadOpen} onOpenChange={setIsAddLeadOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full md:w-auto shrink-0"><Plus className="h-4 w-4 mr-2" /> Adicionar Lead</Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px] max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Adicionar Novo Lead</DialogTitle>
                      <DialogDescription>
                        Selecione um ou mais produtos de carregadores. Cada produto gera uma
                        negociação separada no CRM (um card por produto).
                      </DialogDescription>
                    </DialogHeader>
                    <AdicionarLead
                      onClose={() => setIsAddLeadOpen(false)}
                      onSuccess={() => {
                        fetchLeads();
                        setIsAddLeadOpen(false);
                      }}
                      defaultProduct={PRODUTO_LEAD_WALLBOX}
                      lockProduct={false}
                      allowMultipleProducts
                      allowedProductOptions={[...PRODUTOS_LEAD_CARREGADOR_SEM_LEGADO]}
                    />
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <form
                className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-2 mb-4`}
                onSubmit={(e) => {
                  e.preventDefault();
                  setAppliedSearch(searchQuery);
                }}
              >
                <Input
                  placeholder="Pesquisar Nome ou Razão Social"
                  className={isMobile ? "w-full" : "w-64"}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Button type="submit" variant="outline" className={isMobile ? "w-full" : ""}>
                  <Search className="h-4 w-4 mr-2" /> Buscar
                </Button>
              </form>
              {(isLoadingLeads || isLoading) ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : sortedLeadsPotenciais.length === 0 ? (
                <p className="text-center text-muted-foreground py-10">
                  {appliedSearch
                    ? "Nenhum lead encontrado para a busca informada."
                    : "Nenhum lead de carregadores pendente encontrado."}
                </p>
              ) : isMobile ? (
                /* Vista de cards para mobile */
                <div className="space-y-4">
                  {sortedLeadsPotenciais.map((lead: any) => {
                    const temProposta = propostas?.some(p => p.lead_id === lead.id);
                    return (
                      <Card key={lead.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            {/* Cabeçalho do card com nome e status */}
                            <div className="flex justify-between items-start gap-2">
                              <h3 className="font-semibold text-lg flex-1">
                                {getLeadDisplayName(lead)}
                              </h3>
                              {temProposta ? (
                                <Badge variant="default" className="bg-green-100 text-green-800 border-green-200 shrink-0">Proposta Criada</Badge>
                              ) : (
                                <Badge variant="outline" className="text-muted-foreground shrink-0">Sem Proposta</Badge>
                              )}
                            </div>
                            
                            {/* Informações do lead */}
                            <div className="space-y-2 text-sm text-muted-foreground">
                              <div className="flex flex-wrap items-center gap-2">
                                <Package className="h-4 w-4 shrink-0" />
                                <span className="font-medium text-foreground">Produto:</span>
                                <Badge variant="secondary" className="font-normal text-left h-auto py-1 whitespace-normal max-w-full">
                                  {textoProdutoLeadCarregador(lead.produto)}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 shrink-0" />
                                <span className="font-medium text-foreground">Data de Cadastro: </span>
                                <span>{new Date(lead.created_at).toLocaleDateString('pt-BR')}</span>
                              </div>
                              {lead.telefone && (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-foreground">Telefone: </span>
                                  <span>{lead.telefone}</span>
                                </div>
                              )}
                              {lead.email && (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-foreground">Email: </span>
                                  <span className="break-all">{lead.email}</span>
                                </div>
                              )}
                              {lead.parceiro && (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-foreground">Parceiro: </span>
                                  <span>{lead.parceiro.nome}</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Botões de ação (parceiro pai: apenas visualização para leads da rede) */}
                            <div className="flex flex-col gap-2 pt-2">
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1"
                                  onClick={() => {
                                    setSelectedLead(lead as any);
                                    setIsEditingLead(false);
                                    setIsDetailsOpen(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  Ver Detalhes
                                </Button>
                                {!isLeadFromRede(lead) && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => {
                                      setSelectedLead(lead as any);
                                      setIsEditingLead(true);
                                      setIsDetailsOpen(true);
                                    }}
                                  >
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Editar
                                  </Button>
                                )}
                              </div>
                              {!temProposta && !isLeadFromRede(lead) && (
                                <Button 
                                  variant="default" 
                                  size="sm" 
                                  className="w-full"
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, lead_id: lead.id }));
                                    setActiveTab("new");
                                  }}
                                >
                                  <Calculator className="h-4 w-4 mr-2" />
                                  Criar Proposta
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                /* Vista de tabela para desktop */
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className={`cursor-pointer ${sortColumn === 'nome' ? 'text-primary' : ''}`} onClick={() => handleSort('nome')}>
                        Nome
                        {sortColumn === 'nome' ? (
                          sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                        ) : (
                          <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                        )}
                      </TableHead>
                      <TableHead className={`cursor-pointer min-w-[10rem] max-w-[14rem] ${sortColumn === 'produto' ? 'text-primary' : ''}`} onClick={() => handleSort('produto')}>
                        Produto
                        {sortColumn === 'produto' ? (
                          sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                        ) : (
                          <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                        )}
                      </TableHead>
                      <TableHead className={`cursor-pointer ${sortColumn === 'created_at' ? 'text-primary' : ''}`} onClick={() => handleSort('created_at')}>
                        Data de Cadastro
                        {sortColumn === 'created_at' ? (
                          sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                        ) : (
                          <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                        )}
                      </TableHead>
                      <TableHead className={`cursor-pointer ${sortColumn === 'parceiro' ? 'text-primary' : ''}`} onClick={() => handleSort('parceiro')}>
                        Parceiro
                        {sortColumn === 'parceiro' ? (
                          sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                        ) : (
                          <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                        )}
                      </TableHead>
                      <TableHead>Proposta Criada?</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedLeadsPotenciais.map((lead: any) => {
                      const temProposta = propostas?.some(p => p.lead_id === lead.id);
                      return (
                        <TableRow key={lead.id}>
                          <TableCell className="font-medium">{getLeadDisplayName(lead)}</TableCell>
                          <TableCell className="align-top max-w-[14rem]">
                            <span className="text-sm leading-snug">{textoProdutoLeadCarregador(lead.produto)}</span>
                          </TableCell>
                          <TableCell>{new Date(lead.created_at).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell>
                            {lead.parceiro ? (
                              <span className="text-sm">{lead.parceiro.nome}</span>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {temProposta ? (
                              <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">Sim</Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">Não</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right space-x-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Ver detalhes"
                              onClick={() => {
                                setSelectedLead(lead as any);
                                setIsEditingLead(false);
                                setIsDetailsOpen(true);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {!isLeadFromRede(lead) && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Editar"
                                  onClick={() => {
                                    setSelectedLead(lead as any);
                                    setIsEditingLead(true);
                                    setIsDetailsOpen(true);
                                  }}
                                >
                                  <Pencil className="h-4 w-4 text-blue-600" />
                                </Button>
                                {!temProposta && (
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => {
                                      setFormData(prev => ({ ...prev, lead_id: lead.id }));
                                      setActiveTab("new");
                                    }}
                                  >
                                    <Calculator className="h-4 w-4 mr-2" />
                                    Criar Proposta
                                  </Button>
                                )}
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="clientes" className="mt-6">
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Clientes de Carregadores</CardTitle>
                <CardDescription>Clientes com propostas aceitas ou contratos gerados.</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : sortedLeadsClientes.length === 0 ? (
                <p className="text-center text-muted-foreground py-10">Nenhum cliente convertido encontrado.</p>
              ) : isMobile ? (
                /* Vista de cards para mobile */
                <div className="space-y-4">
                  {sortedLeadsClientes.map((lead: any) => {
                    const propostasDoLead = propostas?.filter(p => p.lead_id === lead.id);
                    const propostaAtiva = propostasDoLead?.find(p => 
                      (p.contratos_carregadores && p.contratos_carregadores.length > 0 && p.contratos_carregadores[0].contrato_pdf_url)
                    ) || propostasDoLead?.find(p => p.status === 'aceita');
                    
                    const statusTexto = (propostaAtiva?.contratos_carregadores && propostaAtiva.contratos_carregadores.length > 0 && propostaAtiva.contratos_carregadores[0].contrato_pdf_url) 
                      ? 'Contrato gerado' 
                      : 'Proposta aceita';

                    return (
                      <Card key={lead.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            {/* Cabeçalho do card com nome e status */}
                            <div className="flex justify-between items-start gap-2">
                              <h3 className="font-semibold text-lg flex-1">
                                {getLeadDisplayName(lead)}
                              </h3>
                              <Badge variant="default" className="bg-green-100 text-green-800 border-green-200 shrink-0">
                                {statusTexto}
                              </Badge>
                            </div>
                            
                            {/* Informações do cliente */}
                            <div className="space-y-2 text-sm text-muted-foreground">
                              <div className="flex flex-wrap items-center gap-2">
                                <Package className="h-4 w-4 shrink-0" />
                                <span className="font-medium text-foreground">Produto:</span>
                                <Badge variant="secondary" className="font-normal text-left h-auto py-1 whitespace-normal max-w-full">
                                  {textoProdutoLeadCarregador(lead.produto)}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 shrink-0" />
                                <span className="font-medium text-foreground">Data de Cadastro: </span>
                                <span>{new Date(lead.created_at).toLocaleDateString('pt-BR')}</span>
                              </div>
                              {lead.telefone && (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-foreground">Telefone: </span>
                                  <span>{lead.telefone}</span>
                                </div>
                              )}
                              {lead.email && (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-foreground">Email: </span>
                                  <span className="break-all">{lead.email}</span>
                                </div>
                              )}
                              {lead.parceiro && (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-foreground">Parceiro: </span>
                                  <span>{lead.parceiro.nome}</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Botões de ação (parceiro pai: só visualização para leads da rede) */}
                            <div className="flex gap-2 pt-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => {
                                  setSelectedLead(lead as any);
                                  setIsEditingLead(false);
                                  setIsDetailsOpen(true);
                                }}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                Ver Detalhes
                              </Button>
                              {!isLeadFromRede(lead) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1"
                                  onClick={() => {
                                    setSelectedLead(lead as any);
                                    setIsEditingLead(true);
                                    setIsDetailsOpen(true);
                                  }}
                                >
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Editar
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                /* Vista de tabela para desktop */
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className={`cursor-pointer ${sortColumn === 'nome' ? 'text-primary' : ''}`} onClick={() => handleSort('nome')}>
                        Nome
                        {sortColumn === 'nome' ? (
                          sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                        ) : (
                          <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                        )}
                      </TableHead>
                      <TableHead className={`cursor-pointer min-w-[10rem] max-w-[14rem] ${sortColumn === 'produto' ? 'text-primary' : ''}`} onClick={() => handleSort('produto')}>
                        Produto
                        {sortColumn === 'produto' ? (
                          sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                        ) : (
                          <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                        )}
                      </TableHead>
                      <TableHead className={`cursor-pointer ${sortColumn === 'created_at' ? 'text-primary' : ''}`} onClick={() => handleSort('created_at')}>
                        Data de Cadastro
                        {sortColumn === 'created_at' ? (
                          sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                        ) : (
                          <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                        )}
                      </TableHead>
                      <TableHead className={`cursor-pointer ${sortColumn === 'telefone' ? 'text-primary' : ''}`} onClick={() => handleSort('telefone')}>
                        Contato
                        {sortColumn === 'telefone' ? (
                          sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                        ) : (
                          <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                        )}
                      </TableHead>
                      <TableHead className={`cursor-pointer ${sortColumn === 'parceiro' ? 'text-primary' : ''}`} onClick={() => handleSort('parceiro')}>
                        Parceiro
                        {sortColumn === 'parceiro' ? (
                          sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                        ) : (
                          <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                        )}
                      </TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedLeadsClientes.map((lead: any) => {
                      const propostasDoLead = propostas?.filter(p => p.lead_id === lead.id);
                      const propostaAtiva = propostasDoLead?.find(p => 
                        (p.contratos_carregadores && p.contratos_carregadores.length > 0 && p.contratos_carregadores[0].contrato_pdf_url)
                      ) || propostasDoLead?.find(p => p.status === 'aceita');
                      
                      const statusTexto = (propostaAtiva?.contratos_carregadores && propostaAtiva.contratos_carregadores.length > 0 && propostaAtiva.contratos_carregadores[0].contrato_pdf_url) 
                        ? 'Contrato gerado' 
                        : 'Proposta aceita';

                      return (
                        <TableRow key={lead.id}>
                          <TableCell className="font-medium">{getLeadDisplayName(lead)}</TableCell>
                          <TableCell className="align-top max-w-[14rem]">
                            <span className="text-sm leading-snug">{textoProdutoLeadCarregador(lead.produto)}</span>
                          </TableCell>
                          <TableCell>{new Date(lead.created_at).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>{lead.telefone}</div>
                              <div className="text-muted-foreground">{lead.email}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {lead.parceiro ? (
                              <span className="text-sm">{lead.parceiro.nome}</span>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                              {statusTexto}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right space-x-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Ver detalhes"
                              onClick={() => {
                                setSelectedLead(lead as any);
                                setIsEditingLead(false);
                                setIsDetailsOpen(true);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {!isLeadFromRede(lead) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Editar"
                                onClick={() => {
                                  setSelectedLead(lead as any);
                                  setIsEditingLead(true);
                                  setIsDetailsOpen(true);
                                }}
                              >
                                <Pencil className="h-4 w-4 text-blue-600" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="list" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Propostas</CardTitle>
              <CardDescription>Lista de todas as propostas de carregadores geradas no sistema.</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : propostas?.length === 0 ? (
                <p className="text-center text-muted-foreground py-10">Nenhuma proposta encontrada.</p>
              ) : (
                <>
                  {/* Vista de cards para mobile */}
                  {isMobile ? (
                    <div className="space-y-4">
                  {propostas?.map((proposta) => {
                    const leadId = proposta.leads?.id || proposta.lead_id;
                    const propostaJaSalva = leadId ? propostasSalvas.has(leadId) : false;
                    const statusTexto = (proposta.contratos_carregadores && proposta.contratos_carregadores.length > 0 && proposta.contratos_carregadores[0].contrato_pdf_url) 
                      ? 'Contrato gerado' 
                      : proposta.status === 'enviada' 
                      ? 'gerada' 
                      : proposta.status === 'aceita' 
                      ? 'Proposta aceita' 
                      : proposta.status;

                    return (
                      <Card key={proposta.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            {/* Cabeçalho do card com cliente e status */}
                            <div className="space-y-1">
                              <div className="flex justify-between items-start gap-2">
                                <h3 className="font-semibold text-lg flex-1">
                                  {getLeadDisplayName(proposta.leads)}
                                </h3>
                                <Badge variant="default" className="bg-green-100 text-green-800 border-green-200 shrink-0">
                                  {statusTexto}
                                </Badge>
                              </div>
                              {isPropostaFromRede(proposta) && proposta.leads?.parceiro?.nome && (
                                <p className="text-xs text-muted-foreground">
                                  Parceiro da rede:{" "}
                                  <span className="font-medium text-foreground">
                                    {proposta.leads.parceiro.nome}
                                  </span>
                                </p>
                              )}
                            </div>
                            
                            {/* Informações da proposta com ícones */}
                            <div className="space-y-2 text-sm text-muted-foreground">
                              <div className="flex items-center gap-2">
                                <Package className="h-4 w-4 shrink-0" />
                                <span className="font-medium text-foreground">Modelo: </span>
                                <span>{rotuloModeloPropostaCarregador(proposta)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Gauge className="h-4 w-4 shrink-0" />
                                <span className="font-medium text-foreground">Potência: </span>
                                <span>{rotuloPotenciaPropostaCarregador(proposta)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Zap className="h-4 w-4 shrink-0" />
                                <span className="font-medium text-foreground">Quantidade: </span>
                                <span>{quantidadePropostaCarregador(proposta)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 shrink-0" />
                                <span>{new Date(proposta.created_at).toLocaleDateString('pt-BR')}</span>
                              </div>
                              <div className="flex items-center justify-between pt-2 border-t">
                                <span className="font-semibold text-foreground flex items-center gap-2">
                                  <FileText className="h-4 w-4" />
                                  Valor Total:
                                </span>
                                <span className="font-bold text-primary text-base">{formatBrazilianCurrency(proposta.valor_total)}</span>
                              </div>
                            </div>
                            
                            {/* Botões de ação (parceiro pai: só visualizar proposta da rede) */}
                            <div className="flex flex-col gap-2 pt-2">
                              {!isPropostaFromRede(proposta) && proposta.status === 'enviada' && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="w-full"
                                  onClick={() => updateStatus(proposta.id, 'aceita')}
                                >
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Marcar como Aceita
                                </Button>
                              )}
                              <div className="flex gap-2">
                                {!isPropostaFromRede(proposta) && !propostaJaSalva && (
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="flex-1"
                                    onClick={() => handleSalvarProposta(proposta)}
                                    disabled={salvandoProposta === proposta.id || !proposta.proposta_pdf_url}
                                  >
                                    {salvandoProposta === proposta.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    ) : (
                                      <Save className="h-4 w-4 mr-2" />
                                    )}
                                    Salvar
                                  </Button>
                                )}
                                <Button 
                                  variant="default" 
                                  size="sm" 
                                  className="flex-1"
                                  onClick={() => {
                                    if (proposta.proposta_pdf_url) {
                                      window.open(proposta.proposta_pdf_url, "_blank");
                                      return;
                                    }
                                    gerarPDF(proposta.id);
                                  }}
                                  disabled={isSubmitting}
                                >
                                  {isSubmitting ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  ) : (
                                    <Download className="h-4 w-4 mr-2" />
                                  )}
                                  PDF
                                </Button>
                                {!isPropostaFromRede(proposta) && (
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="destructive" size="sm">
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>
                                          {proposta.contratos_carregadores?.length > 0 
                                            ? "Excluir Proposta e Contrato?" 
                                            : "Você tem certeza?"}
                                        </AlertDialogTitle>
                                        <AlertDialogDescription>
                                          {proposta.contratos_carregadores?.length > 0 
                                            ? `Esta proposta possui ${proposta.contratos_carregadores.length} contrato(s) vinculado(s). Ao confirmar, a proposta e o(s) contrato(s) serão excluídos permanentemente.`
                                            : "Esta ação não pode ser desfeita. Isso excluirá permanentemente a proposta e removerá os dados de nossos servidores."}
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => deleteMutation.mutate({ 
                                            id: proposta.id, 
                                            cascade: proposta.contratos_carregadores?.length > 0,
                                            leadId: proposta.leads?.id || proposta.lead_id
                                          })}
                                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        >
                                          Excluir
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                    </div>
                  ) : (
                    /* Vista de tabela para desktop */
                    <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Modelo</TableHead>
                      <TableHead>Potência</TableHead>
                      <TableHead>Qtd</TableHead>
                      <TableHead>Valor Total</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {propostas?.map((proposta) => {
                      const leadId = proposta.leads?.id || proposta.lead_id;
                      const propostaJaSalva = leadId ? propostasSalvas.has(leadId) : false;
                      
                      return (
                        <TableRow key={proposta.id}>
                          <TableCell className="font-medium">
                            <div>
                              {getLeadDisplayName(proposta.leads)}
                            </div>
                            {isPropostaFromRede(proposta) && proposta.leads?.parceiro?.nome && (
                              <div className="text-xs text-muted-foreground">
                                Parceiro da rede:{" "}
                                <span className="font-medium text-foreground">
                                  {proposta.leads.parceiro.nome}
                                </span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>{rotuloModeloPropostaCarregador(proposta)}</TableCell>
                          <TableCell>{rotuloPotenciaPropostaCarregador(proposta)}</TableCell>
                          <TableCell>{quantidadePropostaCarregador(proposta)}</TableCell>
                          <TableCell>{formatBrazilianCurrency(proposta.valor_total)}</TableCell>
                          <TableCell>{new Date(proposta.created_at).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell>
                            <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                              {(proposta.contratos_carregadores && proposta.contratos_carregadores.length > 0 && proposta.contratos_carregadores[0].contrato_pdf_url) ? 'Contrato gerado' :
                               proposta.status === 'enviada' ? 'gerada' : 
                               proposta.status === 'aceita' ? 'Proposta aceita' : 
                               proposta.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right space-x-2">
                            {!isPropostaFromRede(proposta) && proposta.status === 'enviada' && (
                              <Button variant="ghost" size="icon" title="Marcar como Aceita" onClick={() => updateStatus(proposta.id, 'aceita')}>
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                            {!isPropostaFromRede(proposta) && !propostaJaSalva && (
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                title="Salvar na aba Documentos" 
                                onClick={() => handleSalvarProposta(proposta)}
                                disabled={salvandoProposta === proposta.id || !proposta.proposta_pdf_url}
                              >
                                {salvandoProposta === proposta.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Save className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              title={proposta.proposta_pdf_url ? "Ver PDF" : "Gerar ou abrir PDF"}
                              onClick={() => {
                                if (proposta.proposta_pdf_url) {
                                  window.open(proposta.proposta_pdf_url, "_blank");
                                  return;
                                }
                                gerarPDF(proposta.id);
                              }}
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                            </Button>
                            {!isPropostaFromRede(proposta) && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="text-destructive">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>
                                      {proposta.contratos_carregadores?.length > 0 
                                        ? "Excluir Proposta e Contrato?" 
                                        : "Você tem certeza?"}
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {proposta.contratos_carregadores?.length > 0 
                                      ? `Esta proposta possui ${proposta.contratos_carregadores.length} contrato(s) vinculado(s). Ao confirmar, a proposta e o(s) contrato(s) serão excluídos permanentemente.`
                                      : "Esta ação não pode ser desfeita. Isso excluirá permanentemente a proposta e removerá os dados de nossos servidores."}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteMutation.mutate({ 
                                      id: proposta.id, 
                                      cascade: proposta.contratos_carregadores?.length > 0,
                                      leadId: proposta.leads?.id || proposta.lead_id
                                    })}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="new" className="mt-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Configuração da Proposta</CardTitle>
                <CardDescription>
                  {isEstudoEvLead
                    ? "Monte a proposta de estudo de viabilidade para infraestrutura de recarga (EV)."
                    : isSaveLead
                      ? "Monte a proposta de implantação de SAVE: ative os itens com os interruptores, separe CELESC e CBMSC e, se quiser, a tabela de distâncias."
                      : isProjetoAprovacaoSaveLead
                        ? "Informe os valores dos projetos contratados. Deixe em R$ 0,00 o que não fizer parte do escopo (ex.: somente CBMSC)."
                        : isWallboxCondominioLead
                          ? "Inclua carregadores, ative os itens complementares e, se quiser, a tabela de distâncias."
                        : "Inclua um ou mais carregadores de potências diferentes e, se necessário, projetos de aprovação e itens complementares."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="lead">Cliente (Lead)</Label>
                      <Select 
                        value={formData.lead_id} 
                        onValueChange={(v) => {
                          setFormData({...formData, lead_id: v});
                          // Os valores serão atualizados automaticamente pelo useEffect quando o lead_id mudar
                        }}
                      >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {leadsSemProposta.map((lead) => (
                          <SelectItem key={lead.id} value={lead.id}>
                            {formatLeadNomeComProduto(lead)}
                          </SelectItem>
                        ))}
                        {leadsSemProposta.length === 0 && (
                          <div className="p-2 text-sm text-muted-foreground text-center">
                            Nenhum lead disponível sem proposta
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.lead_id && isEstudoEvLead && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <CarregadoresMoneyInput
                          id="ev-valor-estudo"
                          label="Valor do estudo de viabilidade (R$)"
                          value={Number(evPropostaForm.valorEstudo) || 0}
                          onValueChange={(n) =>
                            setEvPropostaForm((p) => ({ ...p, valorEstudo: n }))
                          }
                          helperText="Valor por estudo (multiplicado pela quantidade)."
                        />
                        <div className="space-y-2">
                          <Label htmlFor="ev-qtd-estudos">Quantidade</Label>
                          <Input
                            id="ev-qtd-estudos"
                            type="number"
                            min={1}
                            step={1}
                            value={evPropostaForm.qtdEstudos}
                            onChange={(e) => {
                              const n = parseInt(e.target.value, 10);
                              setEvPropostaForm((p) => ({
                                ...p,
                                qtdEstudos: Number.isFinite(n) && n >= 1 ? n : 1,
                              }));
                            }}
                          />
                          <p className="text-xs text-muted-foreground">
                            Número de estudos de viabilidade a emitir.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <Label htmlFor="ev-termografia" className="text-base">
                            Termografia
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Inclui análise termográfica no laudo técnico e soma o valor ao investimento.
                          </p>
                        </div>
                        <Switch
                          id="ev-termografia"
                          checked={evPropostaForm.incluirTermografia}
                          onCheckedChange={(checked) =>
                            setEvPropostaForm((p) => ({ ...p, incluirTermografia: checked }))
                          }
                        />
                      </div>
                      {evPropostaForm.incluirTermografia && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <CarregadoresMoneyInput
                            id="ev-valor-termografia"
                            label="Valor da análise termográfica (R$)"
                            value={Number(evPropostaForm.valorTermografia) || 0}
                            onValueChange={(n) =>
                              setEvPropostaForm((p) => ({ ...p, valorTermografia: n }))
                            }
                            helperText="Valor por análise."
                          />
                          <div className="space-y-2">
                            <Label htmlFor="ev-qtd-termografia">Quantidade</Label>
                            <Input
                              id="ev-qtd-termografia"
                              type="number"
                              min={1}
                              step={1}
                              value={evPropostaForm.qtdTermografia}
                              onChange={(e) => {
                                const n = parseInt(e.target.value, 10);
                                setEvPropostaForm((p) => ({
                                  ...p,
                                  qtdTermografia: Number.isFinite(n) && n >= 1 ? n : 1,
                                }));
                              }}
                            />
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <Label htmlFor="ev-laudo-spda" className="text-base">
                            Laudo SPDA e aterramento
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Inclui o serviço na proposta e soma valor × quantidade ao investimento.
                          </p>
                        </div>
                        <Switch
                          id="ev-laudo-spda"
                          checked={evPropostaForm.incluirLaudoSpda}
                          onCheckedChange={(checked) =>
                            setEvPropostaForm((p) => ({ ...p, incluirLaudoSpda: checked }))
                          }
                        />
                      </div>
                      {evPropostaForm.incluirLaudoSpda && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <CarregadoresMoneyInput
                            id="ev-valor-laudo-spda"
                            label="Valor do laudo SPDA e aterramento (R$)"
                            value={Number(evPropostaForm.valorLaudoSpda) || 0}
                            onValueChange={(n) =>
                              setEvPropostaForm((p) => ({ ...p, valorLaudoSpda: n }))
                            }
                            helperText="Valor unitário."
                          />
                          <div className="space-y-2">
                            <Label htmlFor="ev-qtd-laudo-spda">Quantidade</Label>
                            <Input
                              id="ev-qtd-laudo-spda"
                              type="number"
                              min={1}
                              step={1}
                              value={evPropostaForm.qtdLaudoSpda}
                              onChange={(e) => {
                                const n = parseInt(e.target.value, 10);
                                setEvPropostaForm((p) => ({
                                  ...p,
                                  qtdLaudoSpda: Number.isFinite(n) && n >= 1 ? n : 1,
                                }));
                              }}
                            />
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <Label htmlFor="ev-compra-analisador" className="text-base">
                            Analisador de Energia
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Inclui o analisador de energia na proposta (valor × quantidade).
                          </p>
                        </div>
                        <Switch
                          id="ev-compra-analisador"
                          checked={evPropostaForm.incluirCompraAnalisador}
                          onCheckedChange={(checked) =>
                            setEvPropostaForm((p) => ({ ...p, incluirCompraAnalisador: checked }))
                          }
                        />
                      </div>
                      {evPropostaForm.incluirCompraAnalisador && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <CarregadoresMoneyInput
                            id="ev-valor-compra-analisador"
                            label="Valor do analisador de energia (R$)"
                            value={Number(evPropostaForm.valorCompraAnalisador) || 0}
                            onValueChange={(n) =>
                              setEvPropostaForm((p) => ({ ...p, valorCompraAnalisador: n }))
                            }
                            helperText="Valor unitário."
                          />
                          <div className="space-y-2">
                            <Label htmlFor="ev-qtd-compra-analisador">Quantidade</Label>
                            <Input
                              id="ev-qtd-compra-analisador"
                              type="number"
                              min={1}
                              step={1}
                              value={evPropostaForm.qtdCompraAnalisador}
                              onChange={(e) => {
                                const n = parseInt(e.target.value, 10);
                                setEvPropostaForm((p) => ({
                                  ...p,
                                  qtdCompraAnalisador: Number.isFinite(n) && n >= 1 ? n : 1,
                                }));
                              }}
                            />
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <Label htmlFor="ev-analise-bombeiros" className="text-base">
                            Análise prévia de exigências do Corpo de Bombeiros (IN-23)
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Inclui no escopo do PDF a análise prévia das condições para viabilização das vagas e
                            intervenções necessárias conforme IN-23 e demais INs aplicáveis. Sem custo adicional.
                          </p>
                        </div>
                        <Switch
                          id="ev-analise-bombeiros"
                          checked={evPropostaForm.incluirAnaliseBombeiros}
                          onCheckedChange={(checked) =>
                            setEvPropostaForm((p) => ({ ...p, incluirAnaliseBombeiros: checked }))
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <Label htmlFor="ev-manual-tecnico" className="text-base">
                            Incluir Manual Técnico de instalação e diretrizes
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Se ativado, informe apenas o valor do manual técnico.
                          </p>
                        </div>
                        <Switch
                          id="ev-manual-tecnico"
                          checked={evPropostaForm.incluirManualTecnico}
                          onCheckedChange={(checked) =>
                            setEvPropostaForm((p) => ({ ...p, incluirManualTecnico: checked }))
                          }
                        />
                      </div>
                      {evPropostaForm.incluirManualTecnico && (
                        <CarregadoresMoneyInput
                          id="ev-valor-manual"
                          label="Valor do manual técnico (R$)"
                          value={Number(evPropostaForm.valorManualTecnico) || 0}
                          onValueChange={(n) =>
                            setEvPropostaForm((p) => ({ ...p, valorManualTecnico: n }))
                          }
                        />
                      )}
                      <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <Label htmlFor="ev-incluir-desconto" className="text-base">
                            Incluir desconto na proposta
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Ative para informar desconto em reais. Desligado, a proposta e o PDF não exibem linha de desconto.
                          </p>
                        </div>
                        <Switch
                          id="ev-incluir-desconto"
                          checked={incluirDescontoEstudoEv}
                          onCheckedChange={(checked) => {
                            setIncluirDescontoEstudoEv(checked);
                            if (!checked) {
                              setFormData((fd) => ({
                                ...fd,
                                valor_desconto: 0,
                                desconto_observacao: "",
                              }));
                            }
                          }}
                        />
                      </div>
                    </>
                  )}

                  {formData.lead_id && isSaveLead && (
                    <>
                      <div className="flex items-center justify-between space-x-2">
                        <div className="space-y-0.5">
                          <Label htmlFor="save-incluir-equipamento" className="text-base font-medium">
                            Incluir carregadores veiculares na proposta
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Marque para incluir o fornecimento das estações de recarga (wallbox) no
                            investimento do SAVE. Desmarcado, a proposta não inclui os carregadores
                            das vagas.
                          </p>
                        </div>
                        <Switch
                          id="save-incluir-equipamento"
                          checked={incluirEquipamento}
                          onCheckedChange={setIncluirEquipamento}
                        />
                      </div>
                      {incluirEquipamento ? (
                        <WallboxItensField
                          itens={wallboxItens}
                          onChange={setWallboxItens}
                          modelosDisponiveis={modelosDisponiveis ?? []}
                          incluirEquipamento={incluirEquipamento}
                          defaultsForModel={getEffectiveDefaultsForModel}
                        />
                      ) : null}
                      <WallboxExtrasField
                        extras={wallboxExtrasForm}
                        onChange={setWallboxExtrasForm}
                      />
                      <SaveCargaEstudoField
                        incluirEstudo={savePropostaForm.cargaIncluirEstudo}
                        entrada={{
                          demandaMaximaKw: Number(savePropostaForm.cargaDemandaMaxima) || 0,
                          demandaMinimaKw: Number(savePropostaForm.cargaDemandaMinima) || 0,
                          demandaMediaKw: Number(savePropostaForm.cargaDemandaMedia) || 0,
                          demandaRamalKw: Number(savePropostaForm.cargaDemandaRamal) || 0,
                          qtdPontos: Math.max(1, Number(savePropostaForm.cargaQtdPontos) || 1),
                          potenciaPontoKw:
                            Number(savePropostaForm.cargaPotenciaPonto) ||
                            SAVE_CARGA_POTENCIA_PADRAO_KW,
                          demandaVeraoKw: Number(savePropostaForm.cargaDemandaVerao) || 0,
                        }}
                        onIncluirEstudoChange={(incluir) =>
                          setSavePropostaForm((p) => ({ ...p, cargaIncluirEstudo: incluir }))
                        }
                        onEntradaChange={(entrada) =>
                          setSavePropostaForm((p) => ({
                            ...p,
                            cargaDemandaMaxima: entrada.demandaMaximaKw,
                            cargaDemandaMinima: entrada.demandaMinimaKw,
                            cargaDemandaMedia: entrada.demandaMediaKw,
                            cargaDemandaRamal: entrada.demandaRamalKw,
                            cargaQtdPontos: entrada.qtdPontos,
                            cargaPotenciaPonto: entrada.potenciaPontoKw,
                            cargaDemandaVerao: entrada.demandaVeraoKw,
                          }))
                        }
                      />
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm font-medium">Adequações da implantação</p>
                          <p className="text-xs text-muted-foreground">
                            Ative o item para informar o valor na proposta.
                          </p>
                        </div>
                        <SaveItemSwitchField
                          id="save-adequacoes-eletricas"
                          label="Adequações elétricas Sistema de Recarga"
                          helperText="Adequações da infraestrutura elétrica para o sistema de recarga compartilhada."
                          incluir={savePropostaForm.incluirAdequacoesEletricas}
                          valor={Number(savePropostaForm.precoAdequacoesEletricas) || 0}
                          onIncluirChange={(incluir) =>
                            setSavePropostaForm((p) => ({ ...p, incluirAdequacoesEletricas: incluir }))
                          }
                          onValorChange={(n) =>
                            setSavePropostaForm((p) => ({ ...p, precoAdequacoesEletricas: n }))
                          }
                        />
                        <SaveItemSwitchField
                          id="save-adequacoes-in23"
                          label="Adequações IN-23 (Corpo de Bombeiros)"
                          helperText="Adequações e documentação relacionadas à Instrução Normativa aplicável."
                          incluir={savePropostaForm.incluirAdequacoesIn23}
                          valor={Number(savePropostaForm.precoAdequacoesIn23) || 0}
                          onIncluirChange={(incluir) =>
                            setSavePropostaForm((p) => ({ ...p, incluirAdequacoesIn23: incluir }))
                          }
                          onValorChange={(n) =>
                            setSavePropostaForm((p) => ({ ...p, precoAdequacoesIn23: n }))
                          }
                        />
                        <SaveItemSwitchField
                          id="save-outras-adequacoes"
                          label="Outras Adequações Necessárias"
                          helperText="Demais adequações previstas no escopo comercial desta proposta."
                          incluir={savePropostaForm.incluirOutrasAdequacoes}
                          valor={Number(savePropostaForm.precoOutrasAdequacoes) || 0}
                          onIncluirChange={(incluir) =>
                            setSavePropostaForm((p) => ({ ...p, incluirOutrasAdequacoes: incluir }))
                          }
                          onValorChange={(n) =>
                            setSavePropostaForm((p) => ({ ...p, precoOutrasAdequacoes: n }))
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <Label htmlFor="save-incluir-intervalos-distancia" className="text-base">
                            Incluir intervalos de distância
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Ative para montar a tabela de material e mão de obra por faixa. Desativado, a proposta é gerada sem essa tabela.
                          </p>
                        </div>
                        <Switch
                          id="save-incluir-intervalos-distancia"
                          checked={wbcondoPropostaForm.incluirIntervalos}
                          onCheckedChange={(checked) =>
                            setWbcondoPropostaForm((p) => ({
                              ...p,
                              incluirIntervalos: checked,
                              intervalos:
                                checked && p.intervalos.length === 0
                                  ? intervalosPadraoWallboxCondominio()
                                  : p.intervalos,
                            }))
                          }
                        />
                      </div>
                      {wbcondoPropostaForm.incluirIntervalos ? (
                        <WallboxCondominioIntervalosField
                          intervalos={wbcondoPropostaForm.intervalos}
                          onChange={(intervalos) =>
                            setWbcondoPropostaForm((p) => ({ ...p, intervalos }))
                          }
                        />
                      ) : null}
                      <div className="space-y-2">
                        <Label htmlFor="save-qtd-unidades">Nº de unidades do condomínio</Label>
                        <Input
                          id="save-qtd-unidades"
                          type="number"
                          min={1}
                          step={1}
                          value={qtdUnidadesSaveInput}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "") {
                              setQtdUnidadesSaveInput("");
                              return;
                            }
                            if (!/^\d+$/.test(value)) return;
                            const parsed = parseInt(value, 10);
                            if (parsed < 1) return;
                            setQtdUnidadesSaveInput(value);
                            setSavePropostaForm((p) => ({ ...p, qtdUnidades: parsed }));
                          }}
                        />
                        <p className="text-xs text-muted-foreground">
                          O valor total será dividido por este número (valor por unidade).
                        </p>
                      </div>

                      <div className="space-y-3 rounded-lg border p-3">
                        <div>
                          <p className="text-sm font-medium">Formas de pagamento — etapas</p>
                          <p className="text-xs text-muted-foreground">
                            Entrada, início de obra e conclusão (R$ ou %). A soma deve fechar 100% do total.
                          </p>
                        </div>
                        <SaveEtapaPagamentoField
                          id="save-pgto-entrada"
                          label="Entrada"
                          tipo={savePropostaForm.pgtoEntradaTipo}
                          valor={savePropostaForm.pgtoEntradaValor}
                          valorTotal={valorTotal}
                          onTipoChange={(tipo) =>
                            setSavePropostaForm((p) => ({ ...p, pgtoEntradaTipo: tipo }))
                          }
                          onValorChange={(valor) =>
                            setSavePropostaForm((p) => ({ ...p, pgtoEntradaValor: valor }))
                          }
                        />
                        <SaveEtapaPagamentoField
                          id="save-pgto-inicio"
                          label="Início obra"
                          tipo={savePropostaForm.pgtoInicioTipo}
                          valor={savePropostaForm.pgtoInicioValor}
                          valorTotal={valorTotal}
                          onTipoChange={(tipo) =>
                            setSavePropostaForm((p) => ({ ...p, pgtoInicioTipo: tipo }))
                          }
                          onValorChange={(valor) =>
                            setSavePropostaForm((p) => ({ ...p, pgtoInicioValor: valor }))
                          }
                        />
                        <SaveEtapaPagamentoField
                          id="save-pgto-conclusao"
                          label="Conclusão"
                          tipo={savePropostaForm.pgtoConclusaoTipo}
                          valor={savePropostaForm.pgtoConclusaoValor}
                          valorTotal={valorTotal}
                          onTipoChange={(tipo) =>
                            setSavePropostaForm((p) => ({ ...p, pgtoConclusaoTipo: tipo }))
                          }
                          onValorChange={(valor) =>
                            setSavePropostaForm((p) => ({ ...p, pgtoConclusaoValor: valor }))
                          }
                        />
                        <p
                          className={`text-xs font-medium ${
                            saveEtapasOk ? "text-green-600" : "text-destructive"
                          }`}
                        >
                          {saveEtapasOk
                            ? "Soma das etapas confere com o total."
                            : saveDiferencaEtapas > 0
                              ? `Ajuste os valores para que a soma das etapas seja igual ao total. Faltam ${formatBrazilianCurrency(saveDiferencaEtapas)}.`
                              : `Ajuste os valores para que a soma das etapas seja igual ao total. Excedem ${formatBrazilianCurrency(Math.abs(saveDiferencaEtapas))}.`}
                        </p>
                      </div>

                      <div className="space-y-3 rounded-lg border p-3">
                        <div>
                          <p className="text-sm font-medium">Formas de pagamento — parcelado</p>
                          <p className="text-xs text-muted-foreground">
                            Entrada + até 10x com juros mensais (Tabela Price).
                          </p>
                        </div>
                        <CarregadoresMoneyInput
                          id="save-parc-entrada"
                          label="Entrada (R$)"
                          value={Number(savePropostaForm.parcEntrada) || 0}
                          onValueChange={(n) =>
                            setSavePropostaForm((p) => ({ ...p, parcEntrada: n }))
                          }
                        />
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="save-parc-n">Nº de parcelas</Label>
                            <Select
                              value={String(savePropostaForm.parcNumParcelas)}
                              onValueChange={(v) =>
                                setSavePropostaForm((p) => ({
                                  ...p,
                                  parcNumParcelas: parseInt(v, 10) || 1,
                                }))
                              }
                            >
                              <SelectTrigger id="save-parc-n">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                                  <SelectItem key={n} value={String(n)}>
                                    {n}x
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="save-parc-juros">Juros (% a.m.)</Label>
                            <Input
                              id="save-parc-juros"
                              type="number"
                              min={0}
                              step={0.01}
                              value={
                                savePropostaForm.parcJurosAm === 0
                                  ? ""
                                  : savePropostaForm.parcJurosAm
                              }
                              placeholder="0"
                              onChange={(e) => {
                                const n = parseFloat(e.target.value);
                                setSavePropostaForm((p) => ({
                                  ...p,
                                  parcJurosAm: Number.isFinite(n) && n >= 0 ? n : 0,
                                }));
                              }}
                            />
                          </div>
                        </div>
                        <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
                          <span className="text-muted-foreground">Valor da parcela: </span>
                          <span className="font-semibold">
                            {formatBrazilianCurrency(saveParcValorParcela)}
                          </span>
                          <span className="text-muted-foreground">
                            {" "}
                            ({savePropostaForm.parcNumParcelas}x)
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3 rounded-lg border p-3">
                        <div>
                          <p className="text-sm font-medium">Garantias</p>
                          <p className="text-xs text-muted-foreground">
                            Prazos em meses que aparecem no item de garantias da proposta. Padrão: 18
                            meses de serviço e 60 meses nos materiais Clamper.
                          </p>
                        </div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="save-garantia-servico">Garantia do serviço (meses)</Label>
                            <Input
                              id="save-garantia-servico"
                              type="number"
                              min={1}
                              step={1}
                              value={savePropostaForm.garantiaServicoMeses}
                              onChange={(e) => {
                                const n = parseInt(e.target.value, 10);
                                setSavePropostaForm((p) => ({
                                  ...p,
                                  garantiaServicoMeses: Number.isFinite(n) && n >= 1 ? n : 0,
                                }));
                              }}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="save-garantia-clamper">
                              Garantia dos materiais Clamper (meses)
                            </Label>
                            <Input
                              id="save-garantia-clamper"
                              type="number"
                              min={1}
                              step={1}
                              value={savePropostaForm.garantiaClamperMeses}
                              onChange={(e) => {
                                const n = parseInt(e.target.value, 10);
                                setSavePropostaForm((p) => ({
                                  ...p,
                                  garantiaClamperMeses: Number.isFinite(n) && n >= 1 ? n : 0,
                                }));
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {formData.lead_id && isProjetoAprovacaoSaveLead && (
                    <>
                      <CarregadoresMoneyInput
                        id="psave-projeto-celesc"
                        label="Projeto Celesc (R$)"
                        value={Number(psavePropostaForm.precoCelesc) || 0}
                        onValueChange={(n) =>
                          setPsavePropostaForm((p) => ({ ...p, precoCelesc: n }))
                        }
                        helperText="Deixe em R$ 0,00 se o cliente não contratar o projeto Celesc."
                      />
                      <CarregadoresMoneyInput
                        id="psave-projeto-cbmsc"
                        label="Projeto Corpo de Bombeiros (R$)"
                        value={Number(psavePropostaForm.precoCbmsc) || 0}
                        onValueChange={(n) =>
                          setPsavePropostaForm((p) => ({ ...p, precoCbmsc: n }))
                        }
                        helperText="Deixe em R$ 0,00 se o cliente não contratar o projeto CBMSC."
                      />
                    </>
                  )}

                  {isWallboxItensLead && (
                    <>
                      <div className="flex items-center justify-between space-x-2">
                        <div className="space-y-0.5">
                          <Label htmlFor="incluir-equipamento" className="text-base font-medium">
                            Incluir Wallbox na Proposta
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            {isWallboxCondominioLead
                              ? "Marque para cotar o equipamento do modelo escolhido. Desligado, o PDF não inclui modelo, imagem nem especificações do carregador."
                              : "Marque para incluir o equipamento de cada potência ou deixe desmarcado para proposta só de instalação, materiais e itens complementares."}
                          </p>
                        </div>
                        <Switch
                          id="incluir-equipamento"
                          checked={incluirEquipamento}
                          onCheckedChange={setIncluirEquipamento}
                        />
                      </div>
                      <WallboxItensField
                        itens={wallboxItens}
                        onChange={setWallboxItens}
                        modelosDisponiveis={modelosDisponiveis ?? []}
                        incluirEquipamento={incluirEquipamento}
                        defaultsForModel={getEffectiveDefaultsForModel}
                        catalogo={isWallboxCondominioLead ? modelosCatalogo ?? [] : undefined}
                      />
                      {isWallboxCondominioLead ? (
                        <>
                          <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                            <div className="space-y-0.5">
                              <Label htmlFor="incluir-materiais-wbcondo" className="text-base">
                                Incluir materiais
                              </Label>
                              <p className="text-sm text-muted-foreground">
                                Inclui um custo genérico de materiais de infraestrutura e proteções, sem
                                listar os itens. O valor unitário multiplica a quantidade de carregadores.
                              </p>
                            </div>
                            <Switch
                              id="incluir-materiais-wbcondo"
                              checked={incluirMateriaisWbcondo}
                              onCheckedChange={setIncluirMateriaisWbcondo}
                            />
                          </div>
                          {incluirMateriaisWbcondo ? (
                            <CarregadoresMoneyInput
                              id="preco_mat_wbcondo"
                              label="Materiais (R$)"
                              value={Number(formData.preco_materiais) || 0}
                              onValueChange={(n) =>
                                setFormData({ ...formData, preco_materiais: n })
                              }
                              helperText="Valor unitário, aplicado à quantidade total de carregadores."
                            />
                          ) : null}
                          <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                            <div className="space-y-0.5">
                              <Label htmlFor="incluir-intervalos-distancia" className="text-base">
                                Incluir intervalos de distância
                              </Label>
                              <p className="text-sm text-muted-foreground">
                                Ative para montar a tabela de material e mão de obra por faixa. Desativado, a proposta é gerada sem essa tabela.
                              </p>
                            </div>
                            <Switch
                              id="incluir-intervalos-distancia"
                              checked={wbcondoPropostaForm.incluirIntervalos}
                              onCheckedChange={(checked) =>
                                setWbcondoPropostaForm((p) => ({
                                  ...p,
                                  incluirIntervalos: checked,
                                  intervalos:
                                    checked && p.intervalos.length === 0
                                      ? intervalosPadraoWallboxCondominio()
                                      : p.intervalos,
                                }))
                              }
                            />
                          </div>
                          {wbcondoPropostaForm.incluirIntervalos ? (
                            <WallboxCondominioIntervalosField
                              intervalos={wbcondoPropostaForm.intervalos}
                              onChange={(intervalos) =>
                                setWbcondoPropostaForm((p) => ({ ...p, intervalos }))
                              }
                            />
                          ) : null}
                        </>
                      ) : (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <CarregadoresMoneyInput
                            id="preco_inst_wallbox"
                            label="Mão de obra (R$)"
                            value={Number(formData.preco_instalacao) || 0}
                            onValueChange={(n) =>
                              setFormData({ ...formData, preco_instalacao: n })
                            }
                            helperText="Valor unitário, aplicado à quantidade total de carregadores."
                            readOnly={!canEditValoresInstMateriais}
                            className={
                              canEditValoresInstMateriais
                                ? "space-y-2"
                                : "space-y-2 [&_input]:bg-muted [&_input]:cursor-not-allowed"
                            }
                          />
                          <CarregadoresMoneyInput
                            id="preco_mat_wallbox"
                            label="Materiais (R$)"
                            value={Number(formData.preco_materiais) || 0}
                            onValueChange={(n) =>
                              setFormData({ ...formData, preco_materiais: n })
                            }
                            helperText="Valor unitário, aplicado à quantidade total de carregadores."
                            readOnly={!canEditValoresInstMateriais}
                            className={
                              canEditValoresInstMateriais
                                ? "space-y-2"
                                : "space-y-2 [&_input]:bg-muted [&_input]:cursor-not-allowed"
                            }
                          />
                        </div>
                      )}
                      <WallboxExtrasField
                        extras={wallboxExtrasForm}
                        onChange={setWallboxExtrasForm}
                        incluirMove={isWallboxCondominioLead}
                      />
                    </>
                  )}

                  {(!isEstudoEvLead || incluirDescontoEstudoEv) && (
                    <>
                      <CarregadoresMoneyInput
                        id="valor_desconto"
                        label="Desconto sobre o total (R$)"
                        value={Number(formData.valor_desconto) || 0}
                        onValueChange={(n) =>
                          setFormData({
                            ...formData,
                            valor_desconto: n,
                            ...(n <= 0 ? { desconto_observacao: "" } : {}),
                          })
                        }
                        helperText="Opcional. Desconto em reais sobre a soma dos itens (limitado ao subtotal)."
                      />

                      {descontoSolicitado > 0 && (
                        <div className="space-y-2">
                          <Label htmlFor="desconto_observacao">Observações do desconto (opcional)</Label>
                          <Textarea
                            id="desconto_observacao"
                            rows={2}
                            maxLength={500}
                            placeholder="Ex.: Voucher de desconto de palestra"
                            value={formData.desconto_observacao}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                desconto_observacao: e.target.value.slice(0, 500),
                              })
                            }
                          />
                          <p className="text-xs text-muted-foreground">
                            Texto exibido na proposta entre parênteses na linha do desconto.
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  <div className="space-y-2">
                    <Label
                      htmlFor={
                        isEstudoEvLead
                          ? "detalhamento-escopo"
                          : isSaveLead
                            ? "obs-save"
                            : "obs"
                      }
                    >
                      {isEstudoEvLead
                        ? "Detalhamento do escopo (opcional)"
                        : "Observações"}
                    </Label>
                    {isEstudoEvLead ? (
                      <>
                        <Textarea
                          id="detalhamento-escopo"
                          rows={3}
                          maxLength={2000}
                          placeholder="Observações técnicas ou comerciais complementares…"
                          value={formData.observacoes}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              observacoes: e.target.value.slice(0, 2000),
                            })
                          }
                        />
                        <p className="text-xs text-muted-foreground">
                          Exibido no PDF da proposta, no item 3, abaixo do escopo padrão.
                        </p>
                      </>
                    ) : (
                      <Input
                        id={isSaveLead ? "obs-save" : "obs"}
                        value={formData.observacoes}
                        onChange={(e) =>
                          setFormData({ ...formData, observacoes: e.target.value })
                        }
                      />
                    )}
                  </div>

                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Calculator className="mr-2 h-4 w-4" />{" "}
                        {isEstudoEvLead || isSaveLead || isProjetoAprovacaoSaveLead
                          ? "Salvar proposta"
                          : "Gerar Proposta"}
                      </>
                    )}
                  </Button>

                  {generatedPdfUrl && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="w-full border-primary text-primary hover:bg-primary/10" 
                      onClick={() => window.open(generatedPdfUrl, '_blank')}
                    >
                      <Download className="mr-2 h-4 w-4" /> Baixar Proposta em PDF
                    </Button>
                  )}
                </form>
              </CardContent>
            </Card>

            <Card className="bg-primary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  Resumo da Proposta
                </CardTitle>
                <CardDescription>Confira os valores calculados antes de finalizar.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  {isEstudoEvLead ? (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Quantidade de estudos</span>
                        <span>{qtdEstudosEv}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Estudo de viabilidade (unit.)</span>
                        <span>{formatBrazilianCurrency(Number(evPropostaForm.valorEstudo) || 0)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal estudos</span>
                        <span>
                          {formatBrazilianCurrency(
                            (Number(evPropostaForm.valorEstudo) || 0) * qtdEstudosEv
                          )}
                        </span>
                      </div>
                      {evPropostaForm.incluirTermografia && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Termografia (unit.)</span>
                            <span>
                              {formatBrazilianCurrency(Number(evPropostaForm.valorTermografia) || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Qtd. termografia</span>
                            <span>{qtdTermografiaEv}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Subtotal termografia</span>
                            <span>
                              {formatBrazilianCurrency(
                                (Number(evPropostaForm.valorTermografia) || 0) * qtdTermografiaEv
                              )}
                            </span>
                          </div>
                        </>
                      )}
                      {evPropostaForm.incluirLaudoSpda && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Laudo SPDA e aterramento (unit.)
                            </span>
                            <span>
                              {formatBrazilianCurrency(Number(evPropostaForm.valorLaudoSpda) || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Qtd. laudo SPDA</span>
                            <span>{qtdLaudoSpdaEv}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Subtotal laudo SPDA</span>
                            <span>
                              {formatBrazilianCurrency(
                                (Number(evPropostaForm.valorLaudoSpda) || 0) * qtdLaudoSpdaEv
                              )}
                            </span>
                          </div>
                        </>
                      )}
                      {evPropostaForm.incluirCompraAnalisador && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Analisador de Energia (unit.)</span>
                            <span>
                              {formatBrazilianCurrency(Number(evPropostaForm.valorCompraAnalisador) || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Qtd. analisador de energia</span>
                            <span>{qtdCompraAnalisadorEv}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Subtotal analisador de energia</span>
                            <span>
                              {formatBrazilianCurrency(
                                (Number(evPropostaForm.valorCompraAnalisador) || 0) *
                                  qtdCompraAnalisadorEv
                              )}
                            </span>
                          </div>
                        </>
                      )}
                      {evPropostaForm.incluirAnaliseBombeiros && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Análise prévia Bombeiros (IN-23)</span>
                          <span>Incluso</span>
                        </div>
                      )}
                      {evPropostaForm.incluirManualTecnico && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Manual técnico e diretrizes</span>
                          <span>
                            {formatBrazilianCurrency(Number(evPropostaForm.valorManualTecnico) || 0)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm border-t border-dashed pt-3 mt-2">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span>{formatBrazilianCurrency(valorSubtotalBruto)}</span>
                      </div>
                      {descontoAplicado > 0 && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Desconto</span>
                            <span className="text-red-600">
                              − {formatBrazilianCurrency(descontoAplicado)}
                            </span>
                          </div>
                          {formData.desconto_observacao.trim() ? (
                            <p className="text-xs text-muted-foreground">
                              ({formData.desconto_observacao.trim()})
                            </p>
                          ) : null}
                        </>
                      )}
                      <div className="pt-4 border-t flex justify-between items-center">
                        <span className="font-bold text-lg">
                          {descontoAplicado > 0 ? "Total líquido" : "Total"}
                        </span>
                        <span className="font-bold text-2xl text-primary">
                          {formatBrazilianCurrency(valorTotal)}
                        </span>
                      </div>
                    </>
                  ) : isSaveLead ? (
                    <>
                      {incluirEquipamento ? (
                        <>
                          <div className="flex justify-between text-sm font-medium mb-2">
                            <span className="text-muted-foreground">Carregadores:</span>
                            <span>{somaQuantidadeWallboxItens(wallboxItens)} unidade(s)</span>
                          </div>
                          <div className="space-y-3">
                            {wallboxItens.map((item) => (
                              <div key={item.id} className="flex justify-between text-sm">
                                <span className="text-muted-foreground">
                                  {item.modelo_carregador} ({item.potencia_kw} kW) × {item.quantidade}
                                </span>
                                <span>
                                  {formatBrazilianCurrency(
                                    (Number(item.preco_equipamento) || 0) *
                                      Math.max(1, Number(item.quantidade) || 1)
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Equipamento (total)</span>
                            <span>
                              {formatBrazilianCurrency(somaEquipamentoWallboxItens(wallboxItens))}
                            </span>
                          </div>
                        </>
                      ) : null}
                      {extrasWallboxIncluidos(wallboxExtrasForm).map((extra) => (
                        <div key={extra.key} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{extra.meta.pdfLabel} (unit.)</span>
                            <span>{formatBrazilianCurrency(extra.item.valor)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Qtd. {extra.meta.pdfLabel}</span>
                            <span>{extra.item.quantidade}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Subtotal {extra.meta.pdfLabel}</span>
                            <span>{formatBrazilianCurrency(extra.subtotal)}</span>
                          </div>
                        </div>
                      ))}
                      {savePropostaForm.incluirAdequacoesEletricas ? (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Adequações elétricas</span>
                          <span>
                            {formatBrazilianCurrency(
                              Number(savePropostaForm.precoAdequacoesEletricas) || 0
                            )}
                          </span>
                        </div>
                      ) : null}
                      {savePropostaForm.incluirAdequacoesIn23 ? (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Adequações IN-23</span>
                          <span>
                            {formatBrazilianCurrency(
                              Number(savePropostaForm.precoAdequacoesIn23) || 0
                            )}
                          </span>
                        </div>
                      ) : null}
                      {savePropostaForm.incluirOutrasAdequacoes ? (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Outras adequações</span>
                          <span>
                            {formatBrazilianCurrency(
                              Number(savePropostaForm.precoOutrasAdequacoes) || 0
                            )}
                          </span>
                        </div>
                      ) : null}
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Análise de carga</span>
                        <span>
                          {rotuloSaveCargaModo(
                            savePropostaForm.cargaIncluirEstudo ? "estudo" : "exemplo"
                          )}
                        </span>
                      </div>
                      {wbcondoPropostaForm.incluirIntervalos ? (
                        <div className="mt-3 space-y-2 rounded-md border bg-background/60 p-3">
                          <p className="text-sm font-semibold">Instalação por distância</p>
                          {wbcondoPropostaForm.intervalos.length === 0 ? (
                            <p className="text-xs text-muted-foreground">Nenhum intervalo adicionado.</p>
                          ) : (
                            wbcondoPropostaForm.intervalos.map((intervalo) => (
                              <div key={intervalo.id} className="flex justify-between text-xs">
                                <span className="text-muted-foreground">
                                  {rotuloDistanciaIntervalo(intervalo)}
                                </span>
                                <span className="font-medium">
                                  {formatBrazilianCurrency(totalIntervaloWallboxCondominio(intervalo))}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      ) : null}
                      <div className="flex justify-between text-sm border-t border-dashed pt-3 mt-2">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span>{formatBrazilianCurrency(valorSubtotalBruto)}</span>
                      </div>
                      {descontoAplicado > 0 && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Desconto</span>
                            <span className="text-red-600">
                              − {formatBrazilianCurrency(descontoAplicado)}
                            </span>
                          </div>
                          {formData.desconto_observacao.trim() ? (
                            <p className="text-xs text-muted-foreground">
                              ({formData.desconto_observacao.trim()})
                            </p>
                          ) : null}
                        </>
                      )}
                      <div className="pt-4 border-t flex justify-between items-center">
                        <span className="font-bold text-lg">
                          {descontoAplicado > 0 ? "Total líquido" : "Total do projeto"}
                        </span>
                        <span className="font-bold text-2xl text-primary">
                          {formatBrazilianCurrency(valorTotal)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm pt-2">
                        <span className="text-muted-foreground">
                          Valor por unidade ({qtdUnidadesSave})
                        </span>
                        <span className="font-medium">
                          {formatBrazilianCurrency(valorPorUnidadeSave)}
                        </span>
                      </div>
                      <div className="mt-3 space-y-1 rounded-md border bg-background/60 p-3 text-xs">
                        <p className="font-semibold text-sm">Opção 1 — etapas</p>
                        <p>
                          Entrada:{" "}
                          {formatBrazilianCurrency(
                            saveValorEtapaEmReais(
                              savePropostaForm.pgtoEntradaValor,
                              savePropostaForm.pgtoEntradaTipo,
                              valorTotal
                            )
                          )}
                        </p>
                        <p>
                          Início obra:{" "}
                          {formatBrazilianCurrency(
                            saveValorEtapaEmReais(
                              savePropostaForm.pgtoInicioValor,
                              savePropostaForm.pgtoInicioTipo,
                              valorTotal
                            )
                          )}
                        </p>
                        <p>
                          Conclusão:{" "}
                          {formatBrazilianCurrency(
                            saveValorEtapaEmReais(
                              savePropostaForm.pgtoConclusaoValor,
                              savePropostaForm.pgtoConclusaoTipo,
                              valorTotal
                            )
                          )}
                        </p>
                        <p className="font-semibold text-sm pt-2">Opção 2 — parcelado</p>
                        <p>
                          Entrada:{" "}
                          {formatBrazilianCurrency(Number(savePropostaForm.parcEntrada) || 0)}
                        </p>
                        <p>
                          {savePropostaForm.parcNumParcelas}x de{" "}
                          {formatBrazilianCurrency(saveParcValorParcela)}
                          {Number(savePropostaForm.parcJurosAm) > 0
                            ? ` (juros ${savePropostaForm.parcJurosAm}% a.m.)`
                            : " (sem juros)"}
                        </p>
                      </div>
                    </>
                  ) : isProjetoAprovacaoSaveLead ? (
                    <>
                      {psaveIncluiCelesc && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Projeto Celesc</span>
                          <span>
                            {formatBrazilianCurrency(Number(psavePropostaForm.precoCelesc) || 0)}
                          </span>
                        </div>
                      )}
                      {psaveIncluiCbmsc && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Projeto Corpo de Bombeiros</span>
                          <span>
                            {formatBrazilianCurrency(Number(psavePropostaForm.precoCbmsc) || 0)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm border-t border-dashed pt-3 mt-2">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span>{formatBrazilianCurrency(valorSubtotalBruto)}</span>
                      </div>
                      {descontoAplicado > 0 && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Desconto</span>
                            <span className="text-red-600">
                              − {formatBrazilianCurrency(descontoAplicado)}
                            </span>
                          </div>
                          {formData.desconto_observacao.trim() ? (
                            <p className="text-xs text-muted-foreground">
                              ({formData.desconto_observacao.trim()})
                            </p>
                          ) : null}
                        </>
                      )}
                      <div className="pt-4 border-t flex justify-between items-center">
                        <span className="font-bold text-lg">
                          {descontoAplicado > 0 ? "Total líquido" : "Total"}
                        </span>
                        <span className="font-bold text-2xl text-primary">
                          {formatBrazilianCurrency(valorTotal)}
                        </span>
                      </div>
                    </>
                  ) : isWallboxCondominioLead ? (
                    <>
                      <div className="flex justify-between text-sm font-medium mb-2">
                        <span className="text-muted-foreground">Quantidade:</span>
                        <span>{somaQuantidadeWallboxItens(wallboxItens)} unidade(s)</span>
                      </div>
                      <div className="space-y-3">
                        {wallboxItens.map((item) => (
                          <div key={item.id} className="flex items-center justify-between gap-2 text-sm">
                            <span className="flex items-center gap-2 text-muted-foreground">
                              {item.imagem_url ? (
                                <img
                                  src={item.imagem_url}
                                  alt=""
                                  className="h-8 w-8 rounded object-contain bg-white"
                                />
                              ) : null}
                              {item.modelo_carregador} ({item.potencia_kw} kW)
                            </span>
                            <span>{item.quantidade} un.</span>
                          </div>
                        ))}
                      </div>
                      {incluirEquipamento && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Equipamento (total)</span>
                          <span>{formatBrazilianCurrency(somaEquipamentoWallboxItens(wallboxItens))}</span>
                        </div>
                      )}
                      {incluirMateriaisWbcondo ? (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Materiais (total)</span>
                          <span>
                            {formatBrazilianCurrency(
                              (Number(formData.preco_materiais) || 0) *
                                somaQuantidadeWallboxItens(wallboxItens)
                            )}
                          </span>
                        </div>
                      ) : null}
                      {extrasWallboxIncluidos(wallboxExtrasForm).map((extra) => (
                        <div key={extra.key} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{extra.meta.pdfLabel} (unit.)</span>
                            <span>{formatBrazilianCurrency(extra.item.valor)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Qtd. {extra.meta.pdfLabel}</span>
                            <span>{extra.item.quantidade}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Subtotal {extra.meta.pdfLabel}</span>
                            <span>{formatBrazilianCurrency(extra.subtotal)}</span>
                          </div>
                        </div>
                      ))}
                      {wbcondoPropostaForm.incluirIntervalos ? (
                        <div className="mt-3 space-y-2 rounded-md border bg-background/60 p-3">
                          <p className="text-sm font-semibold">Instalação por distância</p>
                          {wbcondoPropostaForm.intervalos.length === 0 ? (
                            <p className="text-xs text-muted-foreground">Nenhum intervalo adicionado.</p>
                          ) : (
                            wbcondoPropostaForm.intervalos.map((intervalo) => (
                              <div key={intervalo.id} className="flex justify-between text-xs">
                                <span className="text-muted-foreground">
                                  {rotuloDistanciaIntervalo(intervalo)}
                                </span>
                                <span className="font-medium">
                                  {formatBrazilianCurrency(totalIntervaloWallboxCondominio(intervalo))}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      ) : null}
                      <div className="flex justify-between text-sm border-t border-dashed pt-3 mt-2">
                        <span className="text-muted-foreground">Custos adicionais (subtotal)</span>
                        <span>{formatBrazilianCurrency(valorSubtotalBruto)}</span>
                      </div>
                      {descontoAplicado > 0 && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Desconto</span>
                            <span className="text-red-600">
                              − {formatBrazilianCurrency(descontoAplicado)}
                            </span>
                          </div>
                          {formData.desconto_observacao.trim() ? (
                            <p className="text-xs text-muted-foreground">
                              ({formData.desconto_observacao.trim()})
                            </p>
                          ) : null}
                        </>
                      )}
                      <div className="pt-4 border-t flex justify-between items-center">
                        <span className="font-bold text-lg">
                          {descontoAplicado > 0
                            ? "Total líquido dos custos adicionais"
                            : "Total dos custos adicionais"}
                        </span>
                        <span className="font-bold text-2xl text-primary">
                          {formatBrazilianCurrency(valorTotal)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between text-sm font-medium mb-2">
                        <span className="text-muted-foreground">Quantidade:</span>
                        <span>{somaQuantidadeWallboxItens(wallboxItens)} unidade(s)</span>
                      </div>
                      <div className="space-y-3">
                        {wallboxItens.map((item) => (
                          <div key={item.id} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              {item.modelo_carregador} ({item.potencia_kw} kW)
                            </span>
                            <span>{item.quantidade} un.</span>
                          </div>
                        ))}
                      </div>
                      {incluirEquipamento && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Equipamento (total)</span>
                          <span>{formatBrazilianCurrency(somaEquipamentoWallboxItens(wallboxItens))}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Instalação (total)</span>
                        <span>
                          {formatBrazilianCurrency(
                            (Number(formData.preco_instalacao) || 0) *
                              somaQuantidadeWallboxItens(wallboxItens)
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Materiais (total)</span>
                        <span>
                          {formatBrazilianCurrency(
                            (Number(formData.preco_materiais) || 0) *
                              somaQuantidadeWallboxItens(wallboxItens)
                          )}
                        </span>
                      </div>
                      {extrasWallboxIncluidos(wallboxExtrasForm).map((extra) => (
                        <div key={extra.key} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{extra.meta.pdfLabel} (unit.)</span>
                            <span>{formatBrazilianCurrency(extra.item.valor)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Qtd. {extra.meta.pdfLabel}</span>
                            <span>{extra.item.quantidade}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Subtotal {extra.meta.pdfLabel}</span>
                            <span>{formatBrazilianCurrency(extra.subtotal)}</span>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm border-t border-dashed pt-3 mt-2">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span>{formatBrazilianCurrency(valorSubtotalBruto)}</span>
                      </div>
                      {descontoAplicado > 0 && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Desconto</span>
                            <span className="text-red-600">
                              − {formatBrazilianCurrency(descontoAplicado)}
                            </span>
                          </div>
                          {formData.desconto_observacao.trim() ? (
                            <p className="text-xs text-muted-foreground">
                              ({formData.desconto_observacao.trim()})
                            </p>
                          ) : null}
                        </>
                      )}
                      <div className="pt-4 border-t flex justify-between items-center">
                        <span className="font-bold text-lg">
                          {descontoAplicado > 0
                            ? "Total líquido do investimento"
                            : "Total do Investimento"}
                        </span>
                        <span className="font-bold text-2xl text-primary">
                          {formatBrazilianCurrency(valorTotal)}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                <div className="bg-white p-4 rounded-lg border shadow-sm space-y-3">
                  <h4 className="font-semibold text-sm">Próximos Passos:</h4>
                  <ul className="text-xs space-y-2 text-muted-foreground">
                    {isEstudoEvLead ? (
                      <>
                        <li className="flex items-center gap-2">
                          <div className="h-4 w-4 rounded-full bg-primary/20 flex items-center justify-center text-[10px] text-primary font-bold">
                            1
                          </div>
                          A proposta é salva no sistema; PDF dedicado será habilitado depois.
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-4 w-4 rounded-full bg-primary/20 flex items-center justify-center text-[10px] text-primary font-bold">
                            2
                          </div>
                          Acompanhe na aba &quot;Propostas Enviadas&quot;.
                        </li>
                      </>
                    ) : isSaveLead ? (
                      <>
                        <li className="flex items-center gap-2">
                          <div className="h-4 w-4 rounded-full bg-primary/20 flex items-center justify-center text-[10px] text-primary font-bold">
                            1
                          </div>
                          A proposta SAVE será gerada em PDF com as duas formas de pagamento
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-4 w-4 rounded-full bg-primary/20 flex items-center justify-center text-[10px] text-primary font-bold">
                            2
                          </div>
                          Acompanhe o status na aba &quot;Propostas Enviadas&quot;
                        </li>
                      </>
                    ) : isProjetoAprovacaoSaveLead ? (
                      <>
                        <li className="flex items-center gap-2">
                          <div className="h-4 w-4 rounded-full bg-primary/20 flex items-center justify-center text-[10px] text-primary font-bold">
                            1
                          </div>
                          A proposta será gerada em PDF ({psaveEscopoResumo})
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-4 w-4 rounded-full bg-primary/20 flex items-center justify-center text-[10px] text-primary font-bold">
                            2
                          </div>
                          O contrato poderá ser gerado após aceite do cliente
                        </li>
                      </>
                    ) : (
                      <>
                        <li className="flex items-center gap-2">
                          <div className="h-4 w-4 rounded-full bg-primary/20 flex items-center justify-center text-[10px] text-primary font-bold">
                            1
                          </div>
                          A proposta será gerada em formato PDF
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-4 w-4 rounded-full bg-primary/20 flex items-center justify-center text-[10px] text-primary font-bold">
                            2
                          </div>
                          O contrato poderá ser gerado após aceite do cliente
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-4 w-4 rounded-full bg-primary/20 flex items-center justify-center text-[10px] text-primary font-bold">
                            3
                          </div>
                          Acompanhe o status na aba &quot;Propostas Enviadas&quot;
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="contratos" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div>
                  <CardTitle>Contratos de Carregadores Veiculares</CardTitle>
                  <CardDescription>Leads com propostas aceitas prontas para geração de contratos.</CardDescription>
                </div>
                {propostasAceitas && propostasAceitas.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">Exibir:</Label>
                    <Select
                      value={filtroContratosEmAberto ? "em_aberto" : "todos"}
                      onValueChange={(v) => setFiltroContratosEmAberto(v === "em_aberto")}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        <SelectItem value="em_aberto">Em aberto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingContratos ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : propostasAceitas?.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
                  <FileSignature className="h-12 w-12 text-muted-foreground/50" />
                  <p className="font-medium">Nenhuma proposta aceita encontrada</p>
                  <p className="text-sm text-center">
                    As propostas marcadas como aceitas na aba "Propostas Enviadas" aparecerão aqui para geração de contratos.
                  </p>
                </div>
              ) : (
                <>
                  {propostasAceitasFiltradas.length === 0 && propostasAceitas && propostasAceitas.length > 0 ? (
                    <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
                      <p className="font-medium">Nenhum contrato em aberto</p>
                      <p className="text-sm">Altere o filtro para &quot;Todos&quot; para ver todos os contratos.</p>
                    </div>
                  ) : (
                  <>
                  {propostasAceitasFiltradas.length > 0 && (
                    <div className="mb-4 text-sm text-muted-foreground">
                      {filtroContratosEmAberto
                        ? `Contratos em aberto: ${propostasAceitasFiltradas.length}`
                        : `Total de propostas aceitas: ${propostasAceitas.length}`}
                    </div>
                  )}
                  {isMobile ? (
                    /* Vista de cards para mobile */
                    <div className="space-y-4">
                      {propostasAceitasFiltradas?.map((proposta) => {
                        const leadId = proposta.leads?.id || proposta.lead_id;
                        const contratoJaSalvo = leadId ? contratosSalvos.has(leadId) : false;
                        const temContrato = proposta.contratos_carregadores && proposta.contratos_carregadores.length > 0 && proposta.contratos_carregadores[0].contrato_pdf_url;
                        const contrato = proposta.contratos_carregadores?.[0];
                        const statusContrato = contrato?.status;
                        const emAberto = isContratoEmAberto(statusContrato);

                        return (
                          <Card key={proposta.id} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                              <div className="space-y-3">
                                {/* Cabeçalho do card com cliente */}
                                <div className="space-y-1">
                                  <div className="flex justify-between items-start gap-2">
                                    <h3 className="font-semibold text-lg flex-1">
                                      {getLeadDisplayName(proposta.leads)}
                                    </h3>
                                    {temContrato ? (
                                      <Badge
                                        variant="default"
                                        className={emAberto ? "bg-amber-100 text-amber-800 border-amber-200 shrink-0" : "bg-green-100 text-green-800 border-green-200 shrink-0"}
                                      >
                                        {emAberto ? "Em aberto" : "Finalizado"}
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline" className="shrink-0">
                                        Pendente
                                      </Badge>
                                    )}
                                  </div>
                                  {isPropostaFromRede(proposta) && proposta.leads?.parceiro?.nome && (
                                    <p className="text-xs text-muted-foreground">
                                      Parceiro da rede:{" "}
                                      <span className="font-medium text-foreground">
                                        {proposta.leads.parceiro.nome}
                                      </span>
                                    </p>
                                  )}
                                </div>
                                
                                {/* Informações da proposta */}
                                <div className="space-y-2 text-sm text-muted-foreground">
                                  {proposta.leads?.telefone && (
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-foreground">Telefone: </span>
                                      <span>{formatPhone(proposta.leads.telefone)}</span>
                                    </div>
                                  )}
                                  {proposta.leads?.email && (
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-foreground">Email: </span>
                                      <span className="break-all">{proposta.leads.email}</span>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2">
                                    <Package className="h-4 w-4 shrink-0" />
                                    <span className="font-medium text-foreground">Modelo: </span>
                                    <span>{rotuloModeloPropostaCarregador(proposta) || 'N/A'}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-foreground">Quantidade: </span>
                                    <span>{quantidadePropostaCarregador(proposta)}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Gauge className="h-4 w-4 shrink-0" />
                                    <span className="font-medium text-foreground">Potência: </span>
                                    <span>{rotuloPotenciaPropostaCarregador(proposta)}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4 shrink-0" />
                                    <span className="font-medium text-foreground">Data da Proposta: </span>
                                    <span>{new Date(proposta.created_at).toLocaleDateString('pt-BR')}</span>
                                  </div>
                                  <div className="flex items-center justify-between pt-2 border-t">
                                    <span className="font-semibold text-foreground flex items-center gap-2">
                                      <FileText className="h-4 w-4" />
                                      Valor Total:
                                    </span>
                                    <span className="font-bold text-primary text-base">
                                      {proposta.valor_total ? formatBrazilianCurrency(proposta.valor_total) : 'N/A'}
                                    </span>
                                  </div>
                                  {temContrato && contrato && !isPropostaFromRede(proposta) && (
                                    <div className="flex flex-col gap-1.5 pt-2 border-t">
                                      <Label className="text-xs text-muted-foreground">Contrato assinado?</Label>
                                      {podeGerenciarContratos ? (
                                        <Select
                                          value={statusContrato || "contrato_gerado"}
                                          onValueChange={(v) => updateContratoStatusMutation.mutate({ contratoId: contrato.id, status: v })}
                                          disabled={updateContratoStatusMutation.isPending}
                                        >
                                          <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Status" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {STATUS_CONTRATO_CARREGADOR.map((s) => (
                                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      ) : (
                                        <span className="text-sm text-muted-foreground">{getContratoStatusLabel(statusContrato)}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Botões de ação (parceiro pai: só visualização para propostas da rede) */}
                                <div className="flex flex-col gap-2 pt-2">
                                  {temContrato ? (
                                    <>
                                      {podeGerenciarContratos && !isPropostaFromRede(proposta) && !contratoJaSalvo && (
                                        <Button 
                                          variant="outline" 
                                          size="sm" 
                                          className="w-full"
                                          onClick={() => {
                                            handleSalvarContrato(
                                              proposta.contratos_carregadores[0].contrato_pdf_url,
                                              leadId,
                                              getLeadDisplayName(proposta.leads),
                                              proposta.modelo_carregador === PRODUTO_LEAD_ESTUDO_VIABILIDADE_EV
                                            );
                                          }}
                                          disabled={salvandoContrato === leadId}
                                        >
                                          {salvandoContrato === leadId ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                          ) : (
                                            <Save className="h-4 w-4 mr-2" />
                                          )}
                                          Salvar Contrato
                                        </Button>
                                      )}
                                      <div className="flex flex-wrap gap-2">
                                        <Button 
                                          variant="default" 
                                          size="sm" 
                                          className="flex-1"
                                          onClick={() => proposta.contratos_carregadores[0].contrato_pdf_url && window.open(proposta.contratos_carregadores[0].contrato_pdf_url, '_blank')}
                                        >
                                          <Download className="h-4 w-4 mr-2" />
                                          Ver Contrato
                                        </Button>
                                        {podeGerenciarContratos && !isPropostaFromRede(proposta) && (
                                          <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                              <Button variant="destructive" size="sm">
                                                <Trash2 className="h-4 w-4" />
                                              </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                              <AlertDialogHeader>
                                                <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                  Esta ação não pode ser desfeita. Isso excluirá permanentemente o contrato
                                                  e removerá os dados de nossos servidores.
                                                </AlertDialogDescription>
                                              </AlertDialogHeader>
                                              <AlertDialogFooter>
                                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                <AlertDialogAction
                                                  onClick={() => deleteContratoMutation.mutate(proposta.contratos_carregadores[0].id)}
                                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                >
                                                  Excluir
                                                </AlertDialogAction>
                                              </AlertDialogFooter>
                                            </AlertDialogContent>
                                          </AlertDialog>
                                        )}
                                      </div>
                                    </>
                                  ) : !isPropostaFromRede(proposta) && podeGerenciarContratos ? (
                                    <Button 
                                      variant="default" 
                                      size="sm" 
                                      className="w-full"
                                      onClick={() => handleOpenContractForm(proposta)}
                                    >
                                      <FileSignature className="h-4 w-4 mr-2" />
                                      Gerar Contrato
                                    </Button>
                                  ) : null}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full"
                                    onClick={() => {
                                      if (proposta.leads) {
                                        setSelectedLead(proposta.leads as any);
                                        setIsEditingLead(false);
                                        setIsDetailsOpen(true);
                                      }
                                    }}
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    Ver Detalhes do Cliente
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  ) : (
                    /* Vista de tabela para desktop */
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Contato</TableHead>
                          <TableHead>Modelo</TableHead>
                          <TableHead>Qtd</TableHead>
                          <TableHead>Potência</TableHead>
                          <TableHead>Valor Total</TableHead>
                          <TableHead>Data da Proposta</TableHead>
                          <TableHead>Status do contrato</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {propostasAceitasFiltradas?.map((proposta) => {
                          const leadId = proposta.leads?.id || proposta.lead_id;
                          const contratoJaSalvo = leadId ? contratosSalvos.has(leadId) : false;
                          const temContrato = proposta.contratos_carregadores && proposta.contratos_carregadores.length > 0 && proposta.contratos_carregadores[0].contrato_pdf_url;
                          const contrato = proposta.contratos_carregadores?.[0];
                          const statusContrato = contrato?.status;
                          const emAberto = isContratoEmAberto(statusContrato);

                          return (
                            <TableRow key={proposta.id}>
                              <TableCell className="font-medium">
                                <div>
                                  {getLeadDisplayName(proposta.leads)}
                                </div>
                                {isPropostaFromRede(proposta) && proposta.leads?.parceiro?.nome && (
                                  <div className="text-xs text-muted-foreground">
                                    Parceiro da rede:{" "}
                                    <span className="font-medium text-foreground">
                                      {proposta.leads.parceiro.nome}
                                    </span>
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="text-sm">
                                  <div>{proposta.leads?.telefone ? formatPhone(proposta.leads.telefone) : 'N/A'}</div>
                                  <div className="text-muted-foreground text-xs">{proposta.leads?.email || ''}</div>
                                </div>
                              </TableCell>
                              <TableCell>
                                {rotuloModeloPropostaCarregador(proposta) || 'N/A'}
                              </TableCell>
                              <TableCell>
                                {quantidadePropostaCarregador(proposta)}
                              </TableCell>
                              <TableCell>
                                {rotuloPotenciaPropostaCarregador(proposta)}
                              </TableCell>
                              <TableCell>
                                {proposta.valor_total ? formatBrazilianCurrency(proposta.valor_total) : 'N/A'}
                              </TableCell>
                              <TableCell>
                                {new Date(proposta.created_at).toLocaleDateString('pt-BR')}
                              </TableCell>
                              <TableCell>
                                {temContrato && contrato ? (
                                  <div className="flex items-center gap-2">
                                    <Badge
                                      variant="secondary"
                                      className={emAberto ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-green-100 text-green-800 border-green-200"}
                                    >
                                      {emAberto ? "Em aberto" : "Finalizado"}
                                    </Badge>
                                    {!isPropostaFromRede(proposta) && podeGerenciarContratos && (
                                      <Select
                                        value={statusContrato || "contrato_gerado"}
                                        onValueChange={(v) => updateContratoStatusMutation.mutate({ contratoId: contrato.id, status: v })}
                                        disabled={updateContratoStatusMutation.isPending}
                                      >
                                        <SelectTrigger className="w-[140px] h-8">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {STATUS_CONTRATO_CARREGADOR.map((s) => (
                                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    )}
                                    {!isPropostaFromRede(proposta) && !podeGerenciarContratos && (
                                      <span className="text-sm text-muted-foreground">{getContratoStatusLabel(statusContrato)}</span>
                                    )}
                                  </div>
                                ) : (
                                  <Badge variant="outline">Pendente</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right space-x-2">
                                {proposta.contratos_carregadores && proposta.contratos_carregadores.length > 0 && proposta.contratos_carregadores[0].contrato_pdf_url ? (
                                  <>
                                    {podeGerenciarContratos && !isPropostaFromRede(proposta) && !contratoJaSalvo && (
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        title="Salvar na aba Documentos" 
                                        onClick={() => {
                                          handleSalvarContrato(
                                            proposta.contratos_carregadores[0].contrato_pdf_url,
                                            leadId,
                                            getLeadDisplayName(proposta.leads),
                                            proposta.modelo_carregador === PRODUTO_LEAD_ESTUDO_VIABILIDADE_EV
                                          );
                                        }}
                                        disabled={salvandoContrato === leadId}
                                      >
                                        {salvandoContrato === leadId ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                          <Save className="h-4 w-4" />
                                        )}
                                      </Button>
                                    )}
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      title="Ver Contrato" 
                                      onClick={() => proposta.contratos_carregadores[0].contrato_pdf_url && window.open(proposta.contratos_carregadores[0].contrato_pdf_url, '_blank')}
                                    >
                                      <Download className="h-4 w-4 text-primary" />
                                    </Button>
                                    {podeGerenciarContratos && !isPropostaFromRede(proposta) && (
                                      <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                          <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            title="Deletar Contrato"
                                            className="text-destructive"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                          <AlertDialogHeader>
                                            <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                              Esta ação não pode ser desfeita. Isso excluirá permanentemente o contrato
                                              e removerá os dados de nossos servidores.
                                            </AlertDialogDescription>
                                          </AlertDialogHeader>
                                          <AlertDialogFooter>
                                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                            <AlertDialogAction
                                              onClick={() => deleteContratoMutation.mutate(proposta.contratos_carregadores[0].id)}
                                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                            >
                                              Excluir
                                            </AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    )}
                                  </>
                                ) : !isPropostaFromRede(proposta) && podeGerenciarContratos ? (
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    title="Gerar Contrato" 
                                    onClick={() => handleOpenContractForm(proposta)}
                                  >
                                    <FileSignature className="h-4 w-4 text-primary" />
                                  </Button>
                                ) : null}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Ver detalhes do cliente"
                                  onClick={() => {
                                    if (proposta.leads) {
                                      setSelectedLead(proposta.leads as any);
                                      setIsEditingLead(false);
                                      setIsDetailsOpen(true);
                                    }
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {(userProfile?.role === "admin" || userProfile?.role === "parceiro") && (
          <TabsContent value="comissoes" className="mt-6 w-full max-w-full overflow-x-hidden px-2 sm:px-0">
            <ComissoesCarregadoresTab
              isAdmin={userProfile?.role === "admin"}
              parceiroId={userProfile?.id ?? null}
              canManage={permissions.comissoes?.canManage?.() ?? false}
              podeCriarRedeParceiros={userProfile?.pode_criar_rede_parceiros ?? false}
              onHasUnsavedChanges={setConfigHasUnsavedChanges}
            />
          </TabsContent>
        )}
      </Tabs>

      <AlertDialog open={showLeaveConfirm} onOpenChange={(open) => {
        if (!open) {
          setShowLeaveConfirm(false);
          setPendingTab(null);
          // if (blocker?.state === "blocked" && typeof blocker.reset === "function") blocker.reset();
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alterações não salvas</AlertDialogTitle>
            <AlertDialogDescription>
              Você tem alterações não salvas nas configurações. Deseja realmente sair sem salvar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelLeave}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmLeave} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Sair sem salvar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedLead && (
            <ClientDetails 
              cliente={selectedLead} 
              onClose={() => setIsDetailsOpen(false)} 
              onUpdate={() => {
                fetchLeads();
                queryClient.invalidateQueries({ queryKey: ["propostas-carregadores"] });
                queryClient.invalidateQueries({ queryKey: ["propostas-aceitas-carregadores"] });
              }} 
              defaultIsEditing={isLeadFromRede(selectedLead) ? false : isEditingLead}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isContractFormOpen} onOpenChange={setIsContractFormOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedPropostaForContract?.modelo_carregador === PRODUTO_LEAD_ESTUDO_VIABILIDADE_EV
                ? "Gerar contrato — Estudo de Viabilidade (EV)"
                : selectedPropostaForContract?.modelo_carregador === PRODUTO_LEAD_PROJETO_APROVACAO_SAVE
                  ? "Gerar contrato — Projeto e Aprovação de SAVE"
                  : "Gerar contrato de instalação"}
            </DialogTitle>
            <DialogDescription>
              {selectedPropostaForContract?.modelo_carregador === PRODUTO_LEAD_ESTUDO_VIABILIDADE_EV
                ? "Contrato de prestação de serviços de engenharia para laudo técnico com ART. Revise os dados e gere o PDF."
                : selectedPropostaForContract?.modelo_carregador === PRODUTO_LEAD_PROJETO_APROVACAO_SAVE
                  ? "Contrato de engenharia consultiva para projeto e aprovação junto à CELESC e ao CBMSC. Revise os dados e gere o PDF."
                  : "Preencha os dados abaixo para gerar o contrato final em PDF."}
            </DialogDescription>
          </DialogHeader>
          {selectedPropostaForContract && (
            <ContratoCarregadorFormulario 
              proposta={selectedPropostaForContract}
              onCancel={() => setIsContractFormOpen(false)}
              onSuccess={async (pdfUrl) => {
                setIsContractFormOpen(false);
                await Promise.all([
                  queryClient.invalidateQueries({ queryKey: ["propostas-aceitas-carregadores"] }),
                  queryClient.invalidateQueries({ queryKey: ["propostas-carregadores"] }),
                ]);
                toast({ title: "Contrato gerado com sucesso!" });
                window.open(pdfUrl, '_blank');
                setActiveTab("contratos");
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CarregadoresVeiculares;
